import axiosInstance from '../../utils/axiosConfig';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCurrentBranchTime, formatDateInBranchTimezone, convertToBranchTimezone } from '../../utils/timezoneUtils';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Autocomplete,
  SelectChangeEvent,
  Tooltip,
  Chip,
  InputAdornment,
  Snackbar,
  Fade,
  Checkbox,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  LocalMall as CartIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  TableRows as TableRowsIcon,
  Campaign as CampaignIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AddCircle as AddCircleIcon,
  List as ListIcon
} from '@mui/icons-material';
import * as invoiceService from '../../services/invoiceService';
import * as productService from '../../services/productService';
import { getServiceProductByBarcode, createServiceProductOperation } from '../../services/serviceProductService';
import * as paymentMethodService from '../../services/paymentMethodService';
import { getWarehouses } from '../../services/warehouseService';
import { getCustomers, getSuppliers, searchCustomersByPhone, createCustomerWithContext } from '../../services/contactService';
import { checkAutomaticPromotions, applyAutomaticCoupons } from '../../services/automaticPromotionService';
import { getCurrentUserBranch } from '../../services/authService';
import { getCategories } from '../../services/categoryService';
import { apiWithContext } from '../../services/api';
import * as XLSX from 'xlsx';
import taxService from '../../services/taxService';
import { IInvoicePage } from '../../types/invoice';
import { IInvoiceItemExtended, IInvoiceCreateExtended } from '../../types/invoiceExtended';
import { Product, ProductVariant, ProductBarcode } from '../../types/product';
import { Warehouse } from '../../types/warehouse';
import { Contact, ContactFormData } from '../../types/contact';
import { IPaymentMethodUsage } from '../../types/paymentMethod';
import { Category } from '../../types/category';
import { ITax } from '../../types/tax';
import ProductAttributeSearch from './ProductAttributeSearch';
import PaymentMethodsSection from './PaymentMethodsSection';
import MarketingSection from './MarketingSection';
import InvoiceLoyaltyPoints from './InvoiceLoyaltyPoints';
import ImportInvoiceReturnForm from './ImportInvoiceReturnForm';
import CustomerDetailsDialog from './CustomerDetailsDialog';
import CustomerSearchResults from './CustomerSearchResults';
import ServiceProductSearchDialog from './ServiceProductSearchDialog';
import PartialPaymentInput from './PartialPaymentInput';

const InvoiceFormPage = () => {
  const { pageId, invoiceId } = useParams<{ pageId: string; invoiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBranch, toggleBranchDialog, requireBranchSelection } = useAuth();
  const isEditing = invoiceId && invoiceId !== 'new';
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  // استقبال البيانات من WooCommerce
  const wooCommerceData = location.state as {
    fromWooCommerce?: boolean;
    wooOrderId?: string;
    invoiceData?: any;
    warnings?: any[];
  } | null;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidId, setInvalidId] = useState<boolean>(false);
  const [page, setPage] = useState<IInvoicePage | null>(null);
  const [isLoadedForEdit, setIsLoadedForEdit] = useState(false); // متغير لتتبع ما إذا كانت الفاتورة محملة للتعديل
  const [isDateManuallyEdited, setIsDateManuallyEdited] = useState(false); // متغير لتتبع ما إذا تم تعديل التاريخ يدوياً
  const [selectedProductVariants, setSelectedProductVariants] = useState<Record<number, ProductVariant | null>>({});
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [barcode, setBarcode] = useState('');
  const [showPrintButton, setShowPrintButton] = useState<boolean>(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState<string | null>(null);
  const [savedInvoiceNumber, setSavedInvoiceNumber] = useState<string>('');
  const [barcodeMessage, setBarcodeMessage] = useState('');
  const [showBarcodeMessage, setShowBarcodeMessage] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const excelFileInputRef = useRef<HTMLInputElement>(null);
  
  // حالات pagination للجدول
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(50);
  
  // حالات المنتجات غير الموجودة
  const [notFoundProducts, setNotFoundProducts] = useState<Array<{barcode: string, productName: string, rowNumber: number}>>([]);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);
  
  // حالات البحث عن المنتجات
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productSearchResults, setProductSearchResults] = useState<Product[]>([]);
  const [productSearchTimeout, setProductSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // 🔥 إضافة: timeout لفحص الحملات عند تغيير الكمية (debounce)
  const [promotionCheckTimeout, setPromotionCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // حالة إضافة العميل الجديد
  const [addNewCustomer, setAddNewCustomer] = useState(false);
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Contact | null>(null); // تخزين العميل المحدد الكامل
  const [isCustomerDetailsOpen, setIsCustomerDetailsOpen] = useState(false);
  const [isCustomerFieldsLocked, setIsCustomerFieldsLocked] = useState(false);
  const [matchingCustomers, setMatchingCustomers] = useState<Contact[]>([]);
  const [isCustomerSearchOpen, setIsCustomerSearchOpen] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isServiceProductSearchOpen, setIsServiceProductSearchOpen] = useState(false);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [formData, setFormData] = useState<IInvoiceCreateExtended>({
    pageId: pageId || '',
    invoiceNumber: '',
    date: (() => {
      // استخدام توقيت الفرع من activeBranch أو الافتراضي Asia/Riyadh
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
      const branchTime = getCurrentBranchTime(branchTimezone);
      return branchTime.format('YYYY-MM-DDTHH:mm');
    })(),
    status: 'draft',
    items: [],
    subtotal: 0,
    subtotalBeforeTax: 0, // Initialize subtotal before tax
    tax: 0,
    discount: 0,
    discountType: 'fixed', // نوع الخصم: 'fixed' للمبلغ الثابت و 'percentage' للنسبة المئوية
    total: 0,
    notes: '',
    branchId: '', // سيتم تعيينه من خلال الفرع النشط للمستخدم
    paymentMethods: [], // إضافة طرق الدفع كمصفوفة فارغة
    marketingEnabled: false, // تفعيل قسم التسويق
    couponCodes: [] as string[], // أكواد كوبونات التخفيض (مصفوعة)
    couponDiscount: 0, // قيمة الخصم من الكوبون
    couponDiscountType: 'fixed' as 'fixed' | 'percentage', // نوع خصم الكوبون (ثابت أو نسبة)
    appliedCouponDiscount: 0, // قيمة الخصم المطبق فعلياً من الكوبون
    availableCoupons: [] // إضافة الكوبونات المتاحة تلقائياً
  });
  
  // متغيرات جديدة لحفظ قيم الخصم الأصلية قبل الحساب
  const [originalDiscountValue, setOriginalDiscountValue] = useState<number>(0);
  const [originalDiscountType, setOriginalDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [discountInput, setDiscountInput] = useState<string>('0');

  // حالة لتتبع الكوبونات المطبقة
  const [appliedCouponsState, setAppliedCouponsState] = useState<Array<{
    code: string;
    discount: number;
    discountType: string;
    eligibleItems: any[];
    appliedAt: Date;
    discountCalculation?: any;
  }>>([]);

  // References data
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Contact[]>([]);
  const [suppliers, setSuppliers] = useState<Contact[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [taxes, setTaxes] = useState<ITax[]>([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<paymentMethodService.PaymentMethod[]>([]);
  
  // Load data
  useEffect(() => {
    const fetchData = async () => {
      if (!pageId) return;
      
      try {
        setLoading(true);
        
        // Fetch page details
        const pageData = await invoiceService.getInvoicePageById(pageId);
        console.log('🔍 بيانات الصفحة المحملة:', pageData);
        console.log('🏷️ التصنيفات المختارة:', pageData.selectedCategories);
        console.log('📋 نمط اختيار المنتجات:', pageData.productSelectionMode);
        setPage(pageData);
        
        // استخدام الفرع النشط للمستخدم من سياق المصادقة
        if (activeBranch) {
          console.log('استخدام الفرع النشط من السياق:', activeBranch._id);
          
          // Update pageId and branchId in form data
          setFormData(prev => ({
            ...prev,
            pageId,
            branchId: activeBranch._id
          }));
          
          console.log('تم تعيين معرف الفرع في نموذج الفاتورة:', activeBranch._id);
        } else {
          console.error('لم يتم العثور على فرع نشط للمستخدم');
          
          // طلب من المستخدم اختيار فرع
          requireBranchSelection(true);
          
          // إظهار رسالة خطأ
          setError('يجب تحديد فرع نشط قبل إنشاء فاتورة. الرجاء اختيار فرع من القائمة.');
        }
        
        // Fetch products, warehouses, suppliers, categories and taxes in parallel
        // لا نحمل العملاء أو المنتجات هنا - سيتم تحميلهم عند البحث
        const [warehousesData, suppliersData, categoriesData, taxesData] = await Promise.all([
          apiWithContext.get('/warehouses?status=active', 'invoice').then(res => res.data), // المستودعات النشطة مع سياق الفاتورة
          apiWithContext.get('/contacts?type=supplier', 'invoice').then(res => res.data), // الموردين مع سياق الفاتورة
          apiWithContext.get('/categories', 'invoice').then(res => res.data), // التصنيفات مع سياق الفاتورة
          apiWithContext.get('/taxes', 'invoice').then(res => res.data) // الضرائب مع سياق الفاتورة
        ]);
        
        setProducts([]); // ابدأ بقائمة فارغة - سيتم تحميل المنتجات عند البحث
        setWarehouses(warehousesData.warehouses || warehousesData || []);
        setCustomers([]); // ابدأ بقائمة فارغة - سيتم تحميل العملاء عند البحث
        setSuppliers(suppliersData.contacts || suppliersData || []);
        setCategories(categoriesData || []);
        setTaxes(taxesData.data?.taxes || taxesData || []); // إصلاح هيكل بيانات الضرائب
        
        console.log('تم تحميل التصنيفات والضرائب:', {
          categories: categoriesData.length,
          taxes: (taxesData.data?.taxes || taxesData || []).length
        });
        
        
        // If page has default warehouse, set it
        if (pageData.warehouseId) {
          setFormData(prev => ({
            ...prev,
            warehouseId: pageData.warehouseId
          }));
        }
        
        // If page has default status, set it
        if (pageData.defaultStatus) {
          setFormData(prev => ({
            ...prev,
            status: pageData.defaultStatus
          }));
        }
        
        // If editing, fetch invoice data
        if (isEditing) {
          try {
            const invoiceData = await invoiceService.getInvoiceById(invoiceId);
            console.log('بيانات الفاتورة المحملة للتعديل:', invoiceData);
            console.log('معلومات الفاتورة المستردة للتعديل:', {
              discount: invoiceData.discount,
              discountType: invoiceData.discountType || 'fixed'
            });
            
            // تحديث حالة الخصم الأصلية عند التحميل للتعديل
            setOriginalDiscountValue(invoiceData.discount || 0);
            setOriginalDiscountType(invoiceData.discountType || 'fixed');
            setDiscountInput(String(invoiceData.discount || 0));
            
            setFormData({
              pageId,
              invoiceNumber: invoiceData.invoiceNumber,
              date: (() => {
                // عرض التاريخ كما هو محفوظ في قاعدة البيانات (UTC) بدون تحويل للتوقيت المحلي
                const date = new Date(invoiceData.date);
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const hours = String(date.getUTCHours()).padStart(2, '0');
                const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              })(),
              status: invoiceData.status,
              customerId: invoiceData.customerId,
              customerName: invoiceData.customerName,
              supplierId: invoiceData.supplierId,
              supplierName: invoiceData.supplierName,
              warehouseId: invoiceData.warehouseId,
              items: invoiceData.items,
              subtotal: invoiceData.subtotal,
              subtotalBeforeTax: (invoiceData as any).subtotalBeforeTax || 0,
              tax: invoiceData.tax,
              discount: invoiceData.discount,
              discountType: invoiceData.discountType || 'fixed', // استخدام القيمة المخزنة سابقاً أو القيمة الافتراضية
              total: invoiceData.total,
              notes: invoiceData.notes || '',
              branchId: invoiceData.branchId, // استخدام الفرع المرتبط بالفاتورة الموجودة
              paymentMethods: invoiceData.paymentMethods || [], // تضمين طرق الدفع المخزنة سابقاً
              marketingEnabled: (invoiceData as any).marketingEnabled || false,
              couponCodes: (invoiceData as any).couponCodes || [],
              couponDiscount: (invoiceData as any).couponDiscount || 0,
              couponDiscountType: (invoiceData as any).couponDiscountType || 'fixed',
              appliedCouponDiscount: (invoiceData as any).appliedCouponDiscount || 0
            });
            
            // تحميل المبلغ المدفوع للدفع الجزئي
            if (invoiceData.cashPaidAmount) {
              setPaidAmount(invoiceData.cashPaidAmount);
            }
            
            // تحميل بيانات العميل وإضافته إلى قائمة العملاء إذا كان موجوداً
            if (invoiceData.customerId) {
              try {
                const customerResponse = await apiWithContext.get(
                  `/contacts/${invoiceData.customerId}`,
                  'invoice'
                );
                const customerData = customerResponse.data;
                
                if (customerData) {
                  console.log('تم تحميل بيانات العميل للتعديل:', customerData);
                  
                  // إضافة العميل إلى قائمة العملاء
                  setCustomers(prevCustomers => {
                    const existingCustomer = prevCustomers.find(c => c._id === customerData._id);
                    if (!existingCustomer) {
                      console.log(`إضافة العميل ${customerData.name} إلى قائمة Autocomplete للتعديل`);
                      return [...prevCustomers, customerData];
                    }
                    return prevCustomers;
                  });
                  
                  // تحديث حقول العميل المحلية
                  setCustomerName(customerData.name || '');
                  setCustomerPhone(customerData.phone || '');
                  setSelectedCustomer(customerData); // تخزين العميل الكامل
                  setIsCustomerFieldsLocked(true);
                }
              } catch (error) {
                console.error('خطأ في تحميل بيانات العميل:', error);
              }
            }
            
            // عرض طرق الدفع المسترجعة من الخادم للتشخيص
            console.log('طرق الدفع المحملة من الفاتورة:', invoiceData.paymentMethods);
            
            // تحديث متغيرات المنتجات المحددة للعناصر الموجودة
            if (invoiceData.items && invoiceData.items.length > 0) {
              const variantMap: Record<number, ProductVariant | null> = {};
              
              // تحميل المنتجات المطلوبة للتعديل
              const productIdsSet = new Set(invoiceData.items.map((item: any) => item.productId).filter(Boolean));
              const productIds = Array.from(productIdsSet);
              
              if (productIds.length > 0) {
                try {
                  // تحميل المنتجات المطلوبة فقط
                  const productsResponse = await apiWithContext.get(
                    `/products?ids=${productIds.join(',')}&limit=${productIds.length}`,
                    'invoice'
                  );
                  const loadedProducts = productsResponse.data.products || [];
                  
                  invoiceData.items.forEach((item: any, index: number) => {
                    if (item.variantId) {
                      // البحث عن المتغير في المنتجات المحملة
                      for (const product of loadedProducts) {
                        const variant = product.variants.find((v: any) => v._id === item.variantId);
                        if (variant) {
                          variantMap[index] = variant;
                          break;
                        }
                      }
                    }
                  });
                  
                  // إضافة المنتجات المحملة إلى قائمة المنتجات
                  setProducts(loadedProducts);
                } catch (error) {
                  console.error('خطأ في تحميل المنتجات للتعديل:', error);
                }
              }
              
              setSelectedProductVariants(variantMap);
              setIsLoadedForEdit(true); // تعيين أن الفاتورة محملة للتعديل
              
              // حساب المجاميع بدون إعادة حساب خصومات المنتجات (لأنها محملة من قاعدة البيانات)
              setTimeout(() => {
                calculateTotals(
                  invoiceData.items,
                  invoiceData.discount,
                  invoiceData.discountType || 'fixed',
                  true // تجاهل إعادة حساب خصومات المنتجات
                );
              }, 100);
            }
          } catch (err: any) {
            if (err.message.includes('معرف فاتورة غير صالح')) {
              setInvalidId(true);
              setError('معرف الفاتورة غير صالح. الرجاء التحقق من الرابط المستخدم للوصول إلى هذه الصفحة.');
            } else {
              setError(`حدث خطأ أثناء تحميل الفاتورة: ${err.message}`);
            }
          }
        } else {
          // For new invoice, generate a number
          setFormData(prev => ({ 
            ...prev, 
            invoiceNumber: `INV-${Date.now().toString().substring(9)}` 
          }));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('حدث خطأ أثناء تحميل البيانات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [pageId, invoiceId, isEditing]);

  // تحميل بيانات WooCommerce إذا كانت موجودة
  useEffect(() => {
    if (wooCommerceData?.fromWooCommerce && wooCommerceData?.invoiceData) {
      console.log('🛒 تحميل بيانات من WooCommerce:', wooCommerceData);
      
      const data = wooCommerceData.invoiceData;
      
      // تحويل بيانات العميل
      const customerInfo = data.customer || {};
      const customerName = customerInfo.name || customerInfo.customerName || '';
      const customerId = customerInfo.customerId || null;
      const customerPhone = customerInfo.phone || customerInfo.customerPhone || '';
      
      // تحويل المنتجات إلى البنية المطلوبة
      const convertedItems = (data.items || []).map(item => ({
        productId: item.productId || '',
        productName: item.productName || item.name || '',
        variantId: item.variantId || undefined,
        barcode: item.barcode || item.sku || '',
        barcodeId: item.barcodeId || undefined,
        price: parseFloat(item.price) || 0,
        quantity: parseFloat(item.quantity) || 1,
        discount: parseFloat(item.discount) || 0,
        discountType: item.discountType || 'fixed',
        tax: parseFloat(item.tax) || 0,
        total: parseFloat(item.total) || parseFloat(item.price) * parseFloat(item.quantity),
        unit: item.unit || '',
        unitId: item.unitId || '',
        unitName: item.unitName || item.unit || '',
        attributes: item.attributes || {},
        availableQuantity: item.availableQuantity || item.currentStock || 0,
        isServiceProduct: item.isServiceProduct || false,
        isMapped: item.isMapped || false,
        mappingStatus: item.mappingStatus || 'unmapped'
      }));
      
      console.log('📦 المنتجات المحولة:', convertedItems);
      
      // تحديث بيانات الفاتورة
      setFormData(prev => ({
        ...prev,
        customerId: customerId,
        customerName: customerName,
        customerPhone: customerPhone,
        items: convertedItems,
        notes: data.notes || prev.notes,
        subtotal: parseFloat(data.subtotal) || 0,
        tax: parseFloat(data.tax) || 0,
        discount: parseFloat(data.discount) || 0,
        total: parseFloat(data.total) || 0,
        warehouseId: data.warehouse || prev.warehouseId
      }));
      
      // تحديث بيانات العميل في الحقول
      if (customerName) {
        setCustomerName(customerName);
      }
      if (customerPhone) {
        setCustomerPhone(customerPhone);
      }
      
      // إذا كان العميل موجوداً، قفل الحقول
      if (customerId) {
        setIsCustomerFieldsLocked(true);
      }
      
      // إعادة حساب المجاميع
      if (convertedItems.length > 0) {
        setTimeout(() => {
          calculateTotals(convertedItems);
        }, 100);
      }
      
      // عرض التحذيرات إن وجدت
      if (wooCommerceData.warnings && wooCommerceData.warnings.length > 0) {
        console.log('⚠️ التحذيرات:', wooCommerceData.warnings);
        const warningsText = wooCommerceData.warnings
          .map(w => typeof w === 'string' ? w : w.message || JSON.stringify(w))
          .join('\n');
        setBarcodeMessage(`⚠️ تحذيرات:\n${warningsText}`);
        setShowBarcodeMessage(true);
      }
      
      console.log('✅ تم تحميل بيانات WooCommerce بنجاح');
    }
  }, [wooCommerceData]);

  // تحديث التاريخ والوقت تلقائياً كل دقيقة (إذا لم يتم التعديل يدوياً)
  useEffect(() => {
    // لا نحدث التاريخ تلقائياً إذا كانت الفاتورة محملة للتعديل أو تم التعديل يدوياً
    if (isEditing || isDateManuallyEdited) {
      return;
    }

    const intervalId = setInterval(() => {
      setFormData(prev => {
        const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
        const branchTime = getCurrentBranchTime(branchTimezone);
        const newDate = branchTime.format('YYYY-MM-DDTHH:mm');
        
        return {
          ...prev,
          date: newDate
        };
      });
    }, 60000); // تحديث كل 60 ثانية (دقيقة واحدة)

    // تنظيف الـ interval عند إلغاء تحميل المكون
    return () => clearInterval(intervalId);
  }, [isEditing, isDateManuallyEdited, activeBranch]);

  // Focus barcode input when component loads
  useEffect(() => {
    if (!loading && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [loading]);


  // تنظيف timeout عند إلغاء تحميل المكون
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (productSearchTimeout) {
        clearTimeout(productSearchTimeout);
      }
    };
  }, [searchTimeout, productSearchTimeout]);

  // وظيفة البحث عن المنتجات في قاعدة البيانات
  const searchProducts = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProductSearchResults([]);
      return;
    }

    setIsLoadingProducts(true);
    try {
      const response = await apiWithContext.get(
        `/products?search=${encodeURIComponent(searchTerm)}&limit=20`,
        'invoice'
      );
      const searchResults = response.data.products || [];
      setProductSearchResults(searchResults);
      
      // إضافة النتائج إلى قائمة المنتجات الحالية (بدون تكرار)
      setProducts(prevProducts => {
        const existingIds = new Set(prevProducts.map(p => p._id));
        const newProducts = searchResults.filter((p: Product) => !existingIds.has(p._id));
        return [...prevProducts, ...newProducts];
      });
    } catch (error) {
      console.error('خطأ في البحث عن المنتجات:', error);
      setProductSearchResults([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // وظيفة البحث بالباركود
  const searchProductByBarcode = async (barcodeValue: string) => {
    if (!barcodeValue.trim()) return null;

    setIsLoadingProducts(true);
    console.log(`🔍 البحث عن الباركود: ${barcodeValue}`);
    
    try {
      // إرسال الباركود في جسم الطلب بدلاً من URL لتجنب مشاكل الأحرف الخاصة
      const response = await apiWithContext.post(
        `/products/search-by-barcode`,
        { barcode: barcodeValue },
        'invoice'
      );
      
      console.log('🔍 استجابة البحث بالباركود:', response.data);
      
      if (response.data.success && response.data.product) {
        const product = response.data.product;
        const variant = response.data.variant;
        const barcode = response.data.barcode;
        
        console.log(`✅ تم العثور على المنتج: ${product.name}`);
        
        // إضافة المنتج إلى قائمة المنتجات إذا لم يكن موجوداً
        setProducts(prevProducts => {
          const existingProduct = prevProducts.find(p => p._id === product._id);
          if (!existingProduct) {
            return [...prevProducts, product];
          }
          return prevProducts;
        });
        
        return {
          product,
          variant,
          barcode
        };
      }
      
      console.log(`❌ لم يتم العثور على منتج بالباركود: ${barcodeValue}`);
      return null;
    } catch (error) {
      console.error('خطأ في البحث بالباركود:', error);
      return null;
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // معالج البحث مع التأخير
  const handleProductSearch = (searchTerm: string) => {
    setProductSearchTerm(searchTerm);
    
    // إلغاء البحث السابق
    if (productSearchTimeout) {
      clearTimeout(productSearchTimeout);
    }
    
    // تأخير البحث لتجنب الطلبات المتكررة
    const timeout = setTimeout(() => {
      searchProducts(searchTerm);
    }, 300);
    
    setProductSearchTimeout(timeout);
  };
  
  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    
    // إذا تم تعديل حقل التاريخ يدوياً، نوقف التحديث التلقائي
    if (name === 'date') {
      setIsDateManuallyEdited(true);
    }
    
    // تحديث البيانات في النموذج مباشرة
    setFormData(prev => {
      // إنشاء نسخة محدثة من البيانات
      const updatedFormData = { ...prev, [name]: value };
      
      // استدعاء دالة حساب المجاميع فوراً بعد التحديث إذا كان التغيير متعلق بالخصم أو نوعه
      if (name === 'discount') {
        // تحويل قيمة الخصم إلى رقم عند الضرورة
        const numericDiscount = value === '' ? 0 : Number(value);
        updatedFormData.discount = numericDiscount;
        
        // تحديث قيمة الخصم الأصلية
        setOriginalDiscountValue(numericDiscount);
        
        // حساب المجاميع مع القيم المحدثة
        calculateTotals(updatedFormData.items, numericDiscount, updatedFormData.discountType);
      } else if (name === 'discountType') {
        // تسجيل تغيير نوع الخصم للتشخيص
        console.log(`تغيير نوع الخصم من ${prev.discountType} إلى ${value}`);
        
        // تحديث نوع الخصم الأصلي
        setOriginalDiscountType(value as 'fixed' | 'percentage');
        
        // حساب المجاميع مع القيم المحدثة
        calculateTotals(updatedFormData.items, updatedFormData.discount, value as 'fixed' | 'percentage');
      }
      
      return updatedFormData;
    });
  };
  
  // Handle customer selection
  const handleCustomerChange = (customer: Contact | null) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerId: customer._id,
        customerName: customer.name,
        customerPhone: customer.phone
      }));
      // تحديث حقول العميل في واجهة المستخدم وقفلها
      setCustomerName(customer.name || '');
      setCustomerPhone(customer.phone || '');
      setSelectedCustomer(customer); // تخزين العميل الكامل
      setIsCustomerFieldsLocked(true); // قفل الحقول عند اختيار عميل
      setMatchingCustomers([]); // إخفاء قائمة العملاء المطابقة
      
      // إضافة العميل إلى قائمة العملاء إذا لم يكن موجوداً فيها
      setCustomers(prevCustomers => {
        const existingCustomer = prevCustomers.find(c => c._id === customer._id);
        if (!existingCustomer) {
          console.log(`إضافة العميل ${customer.name} إلى قائمة Autocomplete`);
          return [...prevCustomers, customer];
        }
        return prevCustomers;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        customerId: undefined,
        customerName: undefined,
        customerPhone: undefined
      }));
      setCustomerName('');
      setCustomerPhone('');
      setSelectedCustomer(null); // مسح العميل المحدد
      setIsCustomerFieldsLocked(false); // إلغاء قفل الحقول عند عدم اختيار عميل
      setMatchingCustomers([]);
    }
  };

  // البحث الديناميكي عن العملاء من الخادم
  const searchCustomersFromServer = async (searchTerm: string): Promise<Contact[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      return [];
    }

    try {
      setIsLoadingCustomers(true);
      const response = await apiWithContext.get(
        `/contacts?type=customer&search=${encodeURIComponent(searchTerm)}&limit=50`,
        'invoice'
      );
      const customersData = response.data;
      const results = customersData.contacts || customersData || [];
      setCustomers(results);
      return results;
    } catch (error) {
      console.error('خطأ في البحث عن العملاء:', error);
      setCustomers([]);
      return [];
    } finally {
      setIsLoadingCustomers(false);
    }
  };
  
  // البحث عن العملاء بواسطة رقم الهاتف - البحث محلي من قائمة العملاء المحملة
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    console.log(`تم إدخال رقم هاتف: ${phone}`);
    
    setCustomerPhone(phone);
    setCustomerSearchTerm(phone);
    
    // حفظ رقم الهاتف في الفاتورة
    setFormData(prev => ({
      ...prev,
      customerPhone: phone
    }));
    
    // إلغاء التوقيت السابق إذا كان موجودًا
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      console.log('تم إلغاء توقيت البحث السابق');
    }
    
    // تأخير البحث لتجنب العمليات المتكررة أثناء الكتابة
    const timeout = setTimeout(async () => {
      if (phone.trim().length >= 2) {
        try {
          console.log(`بدء البحث عن العملاء برقم الهاتف من الخادم: ${phone}`);
          
          // البحث من الخادم بدلاً من البحث المحلي
          try {
            const response = await apiWithContext.get(
              `/contacts?type=customer&search=${encodeURIComponent(phone)}&limit=20`,
              'invoice'
            );
            const customersData = response.data;
            const matchedCustomers = customersData.contacts || customersData || [];
            
            console.log(`نتائج البحث من الخادم: تم العثور على ${matchedCustomers.length} عميل`);
            console.log('العملاء المطابقون:', matchedCustomers.map(c => `${c.name} (${c.phone})`).join(', '));
            
            // حفظ نتائج البحث
            setMatchingCustomers(matchedCustomers);
            
            // إذا تم العثور على عملاء، افتح نافذة البحث
            if (matchedCustomers.length > 0) {
              console.log(`العثور على ${matchedCustomers.length} عميل مطابق، جاهز لفتح نافذة البحث عند النقر`);
              // لا نفتح النافذة تلقائيًا، بل ننتظر حتى ينقر المستخدم على الشريحة (Chip)
            } else {
              console.log(`لم يتم العثور على عملاء برقم الهاتف: ${phone}`);
              setIsCustomerSearchOpen(false);
            }
          } catch (error) {
            console.error('خطأ في البحث عن العملاء من الخادم:', error);
            setMatchingCustomers([]);
            setIsCustomerSearchOpen(false);
          }
        } catch (error) {
          console.error('خطأ في البحث المحلي عن العملاء:', error);
          setIsCustomerSearchOpen(false);
        }
      } else {
        console.log('الرقم أقل من حرفين، تم مسح نتائج البحث');
        setMatchingCustomers([]);
      }
    }, 300); // تقليل وقت التأخير لتجربة أسرع
    
    console.log('تم تعيين توقيت جديد للبحث');
    setSearchTimeout(timeout);
  };
  
  // معالجة تغيير اسم العميل
  const handleCustomerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCustomerName(name);
    
    // حفظ اسم العميل في الفاتورة
    setFormData(prev => ({
      ...prev,
      customerName: name
    }));
  };
  
  // اختيار عميل من نتائج البحث
  const handleSelectCustomerFromSearch = (customer: Contact) => {
    handleCustomerChange(customer);
    setIsCustomerSearchOpen(false);
  };
  
  // فتح نافذة إضافة عميل جديد
  const handleOpenCustomerDetails = () => {
    setIsCustomerDetailsOpen(true);
  };
  
  // حفظ العميل الجديد
  const handleSaveNewCustomer = async (customerData: ContactFormData) => {
    try {
      // إضافة معرف الفرع إلى بيانات العميل
      if (activeBranch && !customerData.branches?.includes(activeBranch._id)) {
        customerData.branches = [...(customerData.branches || []), activeBranch._id];
      }
      
      // إنشاء العميل الجديد مع سياق الفاتورة
      const newCustomer = await createCustomerWithContext(customerData);
      
      // تحديث حالة الفاتورة بالعميل الجديد
      handleCustomerChange(newCustomer);
      
      // إغلاق نافذة إضافة العميل
      setIsCustomerDetailsOpen(false);
      
      // عرض رسالة نجاح
      setBarcodeMessage(`تم إضافة العميل ${newCustomer.name} بنجاح`);
      setShowBarcodeMessage(true);
    } catch (error: any) {
      console.error('Error creating customer:', error);
      setBarcodeMessage(`حدث خطأ أثناء إضافة العميل: ${error.message}`);
      setShowBarcodeMessage(true);
    }
  };
  
  // تحديث معلومات العميل الموجود
  const handleUpdateCustomer = async (customerId: string, customerData: ContactFormData) => {
    try {
      // إضافة معرف الفرع إلى بيانات العميل
      if (activeBranch && !customerData.branches?.includes(activeBranch._id)) {
        customerData.branches = [...(customerData.branches || []), activeBranch._id];
      }
      
      // تحديث العميل في قاعدة البيانات
      const response = await apiWithContext.put(
        `/contacts/${customerId}`,
        customerData,
        'invoice'
      );
      
      const updatedCustomer = response.data;
      
      // تحديث حالة الفاتورة بالعميل المحدث
      handleCustomerChange(updatedCustomer);
      
      // إغلاق نافذة تحديث العميل
      setIsCustomerDetailsOpen(false);
      
      // عرض رسالة نجاح
      setBarcodeMessage(`تم تحديث معلومات العميل ${updatedCustomer.name} بنجاح`);
      setShowBarcodeMessage(true);
    } catch (error: any) {
      console.error('Error updating customer:', error);
      setBarcodeMessage(`حدث خطأ أثناء تحديث معلومات العميل: ${error.message}`);
      setShowBarcodeMessage(true);
    }
  };
  
  // Handle supplier selection
  const handleSupplierChange = (supplier: Contact | null) => {
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplier._id,
        supplierName: supplier.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        supplierId: undefined,
        supplierName: undefined
      }));
    }
  };
  
  // Handle warehouse selection
  const handleWarehouseChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({ 
      ...prev, 
      warehouseId: event.target.value 
    }));
  };

  // دالة مساعدة لاستخراج السعر المناسب بناءً على نوع السعر المحدد في إعدادات الصفحة
  const getPriceByType = (barcode: ProductBarcode | null | undefined, priceType: string): number => {
    if (!barcode) {
      console.warn('getPriceByType: barcode is null or undefined');
      return 0;
    }
    
    switch (priceType) {
      case 'retailPrice':
        return barcode.retailPrice || 0;
      case 'wholesalePrice':
        return barcode.wholesalePrice || 0;
      case 'customPrice1':
        return barcode.customPrice1 || 0;
      case 'customPrice2':
        return barcode.customPrice2 || 0;
      case 'lastCost':
        return barcode.lastCost || 0;
      case 'averageCost':
        return barcode.averageCost || 0;
      default:
        return barcode.retailPrice || 0;
    }
  };

  // دالة مساعدة للتحقق من أن المنتج مسموح حسب إعدادات التصنيفات
  const isProductAllowed = (product: Product, variant: ProductVariant): boolean => {
    console.log(`🔍 Barcode - فحص المنتج: ${product.name}`);
    console.log(`📋 Barcode - نمط الاختيار: ${page?.productSelectionMode}`);
    console.log(`🏷️ Barcode - التصنيفات المختارة: ${JSON.stringify(page?.selectedCategories)}`);
    
    // إذا كان نمط الاختيار "all" أو لا توجد تصنيفات محددة، اسمح بجميع المنتجات
    if (page?.productSelectionMode !== 'category' || !page?.selectedCategories || page.selectedCategories.length === 0) {
      console.log(`✅ Barcode - السماح بالمنتج ${product.name} - نمط "الكل" أو لا توجد تصنيفات`);
      return true;
    }

    console.log(`🔍 Barcode - فحص تصنيفات المتغير: ${JSON.stringify(variant.categories)}`);

    // التحقق من تصنيفات المتغير
    if (variant.categories && variant.categories.length > 0) {
      const hasMatchingCategory = variant.categories.some(categoryId => {
        const isMatch = page.selectedCategories!.includes(categoryId);
        console.log(`🔍 Barcode - مقارنة تصنيف المتغير ${categoryId} مع المختارة: ${isMatch}`);
        return isMatch;
      });
      if (hasMatchingCategory) {
        console.log(`✅ Barcode - السماح بالمنتج ${product.name} - تطابق في تصنيفات المتغير`);
        return true;
      }
    }

    // التحقق من تصنيفات الباركودات في المتغير
    const barcodeMatch = variant.barcodes.some(barcode => {
      console.log(`🔍 Barcode - فحص تصنيفات الباركود ${barcode.code}: ${JSON.stringify(barcode.categories)}`);
      if (barcode.categories && barcode.categories.length > 0) {
        return barcode.categories.some(categoryId => {
          const isMatch = page.selectedCategories!.includes(categoryId);
          console.log(`🔍 Barcode - مقارنة تصنيف الباركود ${categoryId} مع المختارة: ${isMatch}`);
          return isMatch;
        });
      }
      return false;
    });

    if (barcodeMatch) {
      console.log(`✅ Barcode - السماح بالمنتج ${product.name} - تطابق في تصنيفات الباركود`);
      return true;
    }

    console.log(`❌ Barcode - رفض المنتج ${product.name} - لا يوجد تطابق في التصنيفات`);
    console.log(`❌ Barcode - تصنيفات المتغير: ${JSON.stringify(variant.categories)}`);
    console.log(`❌ Barcode - تصنيفات الباركودات: ${JSON.stringify(variant.barcodes.map(b => b.categories))}`);
    return false;
  };

  // Handle barcode scan
  const handleBarcodeScan = async () => {
    if (!barcode.trim()) return;

    console.log(`🔍 بدء البحث عن الباركود: ${barcode.trim()}`);
    console.log(`📦 عدد المنتجات المتاحة: ${products.length}`);

    try {
      // البحث المباشر في قاعدة البيانات
      const searchResult = await searchProductByBarcode(barcode.trim());
      
      if (searchResult) {
        const { product: foundProduct, variant: foundVariant, barcode: foundBarcode } = searchResult;
        
        // التحقق من وجود البيانات المطلوبة
        if (!foundProduct || !foundVariant) {
          console.error('بيانات ناقصة من البحث:', { foundProduct, foundVariant, foundBarcode });
          setBarcodeMessage('خطأ في بيانات المنتج المسترجعة');
          setShowBarcodeMessage(true);
          setTimeout(() => setShowBarcodeMessage(false), 3000);
          return;
        }
        
        // إذا لم يتم إرجاع foundBarcode، ابحث عن الباركود في المتغير
        let actualBarcode = foundBarcode;
        if (!actualBarcode && foundVariant.barcodes && foundVariant.barcodes.length > 0) {
          // البحث عن الباركود المطابق في قائمة باركودات المتغير
          actualBarcode = foundVariant.barcodes.find(b => b.code === barcode.trim()) ||
                         foundVariant.barcodes.find(b => b.isDefault) ||
                         foundVariant.barcodes[0];
          console.log('تم العثور على الباركود في المتغير:', actualBarcode);
        }
        
        if (!actualBarcode) {
          console.error('لم يتم العثور على باركود صالح');
          setBarcodeMessage('خطأ: لم يتم العثور على باركود صالح للمنتج');
          setShowBarcodeMessage(true);
          setTimeout(() => setShowBarcodeMessage(false), 3000);
          return;
        }
        
        // التحقق من أن المنتج مسموح في هذه الصفحة
        if (isProductAllowed(foundProduct, foundVariant)) {
          console.log(`✅ تم العثور على المنتج: ${foundProduct.name}`);
          
          // استخراج معدل الضريبة من التصنيف الافتراضي فقط
          let taxRate = 0;
          
          // البحث عن التصنيف الافتراضي للباركود أولاً، ثم المتغير
          const defaultCategoryId = actualBarcode?.defaultCategory ||
                                   (foundVariant.barcodes && foundVariant.barcodes.length > 0
                                     ? foundVariant.barcodes.find(b => b.defaultCategory)?.defaultCategory
                                     : undefined);
          
          // الحصول على معدل الضريبة من التصنيف الافتراضي فقط
          if (defaultCategoryId) {
            taxRate = getTaxRateFromCategory(defaultCategoryId);
            console.log(`تم العثور على معدل ضريبة ${taxRate}% من التصنيف الافتراضي ${defaultCategoryId} للمنتج المضاف بالباركود`);
          } else {
            console.log(`لا يوجد تصنيف افتراضي للمنتج ${foundProduct.name} - معدل الضريبة = 0%`);
          }
          
          // استخدام نوع السعر المحدد في إعدادات الصفحة
          const selectedPrice = getPriceByType(actualBarcode, page?.defaultPriceType || 'retailPrice');
          
          // فحص الحملات التلقائية للمنتج
          let finalPrice = selectedPrice;
          let automaticDiscount = 0;
          let promotionMessage = '';
          
          try {
            if (page?._id && foundVariant._id) {
              console.log(`🔍 فحص الحملات التلقائية للمنتج: ${foundProduct.name}`);
              const promotionResult = await checkAutomaticPromotions(
                foundVariant._id,
                page._id,
                1,
                selectedPrice
              );
              
              if (promotionResult.hasPromotions && promotionResult.coupons.length > 0) {
                console.log(`✅ تم العثور على ${promotionResult.coupons.length} كوبون تلقائي من الحملات للمنتج: ${foundProduct.name}`);
                
                // إضافة الكوبونات الجديدة للكوبونات الموجودة (بدون تكرار)
                setFormData(prev => {
                  const existingCoupons = prev.availableCoupons || [];
                  const newCoupons = promotionResult.coupons.filter(
                    newCoupon => !existingCoupons.some(existing => existing.code === newCoupon.code)
                  ).map(coupon => ({
                    code: coupon.code,
                    description: coupon.description,
                    promotionName: coupon.promotionName,
                    discountType: coupon.discountType,
                    discountValue: coupon.discountValue
                  }));
                  
                  const updatedCoupons = [...existingCoupons, ...newCoupons];
                  
                  const newData = {
                    ...prev,
                    marketingEnabled: true,
                    // إضافة الكوبونات الجديدة للموجودة بدلاً من الاستبدال
                    availableCoupons: updatedCoupons
                  };
                  console.log(`📤 إضافة ${newCoupons.length} كوبون جديد للمنتج: ${foundProduct.name}. الإجمالي: ${updatedCoupons.length}`);
                  return newData;
                });
                
                promotionMessage = ` (${promotionResult.coupons.length} كوبون للمنتج الحالي)`;
                console.log(`🎫 تم تجهيز ${promotionResult.coupons.length} كوبون للمنتج: ${foundProduct.name}`);
              } else {
                // إذا لم توجد حملات للمنتج الحالي، مسح الكوبونات المتاحة
                setFormData(prev => ({
                  ...prev,
                  availableCoupons: []
                }));
                console.log(`🧹 تم مسح الكوبونات المتاحة - لا توجد حملات للمنتج: ${foundProduct.name}`);
              }
            }
          } catch (error) {
            console.error('خطأ في فحص الحملات التلقائية:', error);
            // في حالة الخطأ، نستمر بالسعر الأصلي
          }
          
          // البحث عن منتج موجود بنفس المتغير
          const existingItemIndex = formData.items.findIndex(
            item => item.variantId === foundVariant._id
          );

          let updatedItems: IInvoiceItemExtended[];
          
          if (existingItemIndex !== -1) {
            // دمج الكمية مع المنتج الموجود
            updatedItems = formData.items.map((item, index) => {
              if (index === existingItemIndex) {
                const newQuantity = item.quantity + 1;
                return {
                  ...item,
                  quantity: newQuantity,
                  total: item.price * newQuantity,
                  quantityChanged: true // علامة لإعادة حساب الخصم
                };
              }
              return item;
            });
            console.log(`🔄 تم دمج الكمية للمنتج: ${foundProduct.name}`);
          } else {
            // إضافة منتج جديد
            const newItem: IInvoiceItemExtended = {
              productId: foundProduct._id,
              productName: foundProduct.name,
              variantId: foundVariant._id,
              barcode: actualBarcode.code || '',
              barcodeId: actualBarcode._id || '',
              price: finalPrice,
              quantity: 1,
              discount: automaticDiscount,
              discountType: 'fixed',
              tax: taxRate,
              total: finalPrice,
              unit: foundVariant.unit || '',
              unitId: '',
              unitName: actualBarcode.unit || foundVariant.unit || '',
              attributes: foundVariant.attributes || {},
              availableQuantity: foundVariant.quantity || 0,
              isServiceProduct: false
            };
            updatedItems = [...formData.items, newItem];
            console.log(`➕ تم إضافة منتج جديد: ${foundProduct.name}`);
          }

          setFormData(prev => ({ ...prev, items: updatedItems }));
          
          // تحديث متغيرات المنتجات المحددة
          setSelectedProductVariants(prev => ({
            ...prev,
            [updatedItems.length - 1]: foundVariant
          }));

          // إعادة حساب المجاميع
          calculateTotals(updatedItems);

          setBarcodeMessage(`تم إضافة ${foundProduct.name} بنجاح${promotionMessage}`);
          setShowBarcodeMessage(true);
          setTimeout(() => setShowBarcodeMessage(false), 3000); // زيادة الوقت لعرض رسالة الحملة
        } else {
          console.log(`❌ المنتج ${foundProduct.name} غير مسموح في هذه الصفحة`);
          setBarcodeMessage(`المنتج "${foundProduct.name}" غير مسموح في هذه الصفحة`);
          setShowBarcodeMessage(true);
          setTimeout(() => setShowBarcodeMessage(false), 3000);
        }
      } else {
        console.log(`❌ لم يتم العثور على منتج بالباركود: ${barcode.trim()}`);
        
        // البحث عن منتج خدمة بالباركود
        if (page?.enableServiceProducts) {
          try {
            const serviceProductResponse = await getServiceProductByBarcode(barcode.trim());
            const serviceProduct = serviceProductResponse.data;

            if (serviceProduct) {
              console.log(`✅ تم العثور على منتج خدمة: ${serviceProduct.name}`);
              
              // إضافة منتج الخدمة إلى الفاتورة
              const newServiceItem: IInvoiceItemExtended = {
                productId: serviceProduct._id,
                productName: serviceProduct.name,
                barcode: serviceProduct.barcode,
                price: serviceProduct.price,
                quantity: 1,
                discount: 0,
                tax: 0,
                total: serviceProduct.price,
                unit: 'خدمة',
                unitName: 'خدمة',
                attributes: {},
                availableQuantity: 0,
                isServiceProduct: true
              };

              const updatedItems = [...formData.items, newServiceItem];
              setFormData(prev => ({ ...prev, items: updatedItems }));
              calculateTotals(updatedItems);

              setBarcodeMessage(`تم إضافة منتج الخدمة ${serviceProduct.name} بنجاح`);
              setShowBarcodeMessage(true);
              setTimeout(() => setShowBarcodeMessage(false), 2000);
            } else {
              setBarcodeMessage(`لم يتم العثور على منتج بالباركود: ${barcode.trim()}`);
              setShowBarcodeMessage(true);
              setTimeout(() => setShowBarcodeMessage(false), 3000);
            }
          } catch (serviceError) {
            console.error('خطأ في البحث عن منتج الخدمة:', serviceError);
            setBarcodeMessage(`لم يتم العثور على منتج بالباركود: ${barcode.trim()}`);
            setShowBarcodeMessage(true);
            setTimeout(() => setShowBarcodeMessage(false), 3000);
          }
        } else {
          setBarcodeMessage(`لم يتم العثور على منتج بالباركود: ${barcode.trim()}`);
          setShowBarcodeMessage(true);
          setTimeout(() => setShowBarcodeMessage(false), 3000);
        }
      }
    } catch (error) {
      console.error('خطأ في البحث عن المنتج:', error);
      setBarcodeMessage('حدث خطأ أثناء البحث عن المنتج');
      setShowBarcodeMessage(true);
      setTimeout(() => setShowBarcodeMessage(false), 3000);
    }

    // Clear barcode field and refocus
    setBarcode('');
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  // Handle pressing Enter in barcode field
  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan();
    }
  };
  
  // Add item to invoice and open product search dialog
  const handleAddItem = () => {
    const newItem: IInvoiceItemExtended = {
      productId: '',
      productName: '',
      barcode: '',
      price: 0,
      originalPrice: 0, // حفظ السعر الأصلي
      quantity: 1,
      discount: 0,
      discountType: 'fixed', // نوع الخصم: 'fixed' للمبلغ الثابت و 'percentage' للنسبة المئوية
      tax: 0,
      total: 0,
      originalTotal: 0, // إجمالي المنتج الأصلي (السعر × الكمية) قبل أي خصومات
      variantId: undefined,
      barcodeId: undefined,
      unit: '',
      availableQuantity: 0
    };
    
    // إعادة تعيين حالة التحميل للتعديل عند إضافة منتج جديد
    setIsLoadedForEdit(false);
    
    // Calculate the index of the new item before adding it
    const newItemIndex = formData.items.length;
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    // Open the product search dialog directly for the new item
    setSelectedItemIndex(newItemIndex);
    setIsProductSearchOpen(true);
  };
  
  // Remove item from invoice
  const handleRemoveItem = (index: number) => {
    const removedItem = formData.items[index];
    console.log(`🗑️ حذف المنتج: ${removedItem.productName}`);
    console.log(`🔍 بيانات المنتج المحذوف:`, {
      eligibleForCoupon: removedItem.eligibleForCoupon,
      discount: removedItem.discount,
      variantId: removedItem.variantId,
      productId: removedItem.productId
    });
    
    // حذف المنتج من القائمة أولاً
    const updatedItems = formData.items.filter((_, i) => i !== index);
    
    // التحقق من الكوبونات المتاحة وتحديد أي منها يجب حذفه
    const availableCoupons = formData.availableCoupons || [];
    
    // التحقق: إذا كان المنتج مؤهل للكوبون أو له خصم
    const hasDiscount = removedItem.discount && removedItem.discount > 0;
    const isEligible = removedItem.eligibleForCoupon || hasDiscount;
    
    if (availableCoupons.length > 0 && isEligible) {
      console.log(`🔍 فحص الكوبونات المتاحة (${availableCoupons.length}) - المنتج المحذوف كان مؤهل للكوبون أو له خصم`);
      
      // البحث عن الكوبونات المرتبطة بالمنتج المحذوف
      // المنطق: إذا كان المنتج المحذوف مؤهل للكوبون (eligibleForCoupon = true)
      // وكان له خصم، فهذا يعني أنه جزء من حملة كومبو أو X+Y
      const couponsToRemove: string[] = [];
      
      availableCoupons.forEach((coupon: any) => {
        const description = (coupon.description || '').toLowerCase();
        const promotionName = (coupon.promotionName || '').toLowerCase();
        
        // التحقق إذا كان كوبون كومبو أو X+Y
        const isCombo = description.includes('كومبو') || description.includes('combo') ||
                       promotionName.includes('كومبو') || promotionName.includes('combo');
        
        const isBuyXGetY = description.includes('اكس') || promotionName.includes('اكس') ||
                          description.includes('buy_x_get_y') || promotionName.includes('buy_x_get_y') ||
                          (description.includes('اشتر') && description.includes('احصل')) ||
                          (description.includes('buy') && description.includes('get'));
        
        if (isCombo || isBuyXGetY) {
          // التحقق: هل بقي أي منتج آخر من نفس الحملة؟
          // إذا لم يبقَ أي منتج مؤهل لهذا الكوبون، احذفه
          const remainingEligibleItems = updatedItems.filter(item => item.eligibleForCoupon);
          
          // إذا لم يبقَ أي منتج مؤهل، احذف الكوبون
          if (remainingEligibleItems.length === 0) {
            console.log(`🎯 الكوبون ${coupon.code} - لم يبقَ أي منتج مؤهل - سيتم حذفه`);
            couponsToRemove.push(coupon.code);
          } else {
            console.log(`✅ الكوبون ${coupon.code} - يوجد ${remainingEligibleItems.length} منتج مؤهل - سيتم الاحتفاظ به`);
          }
        }
      });
      
      if (couponsToRemove.length > 0) {
        console.log(`🗑️ سيتم حذف ${couponsToRemove.length} كوبون مرتبط بالمنتج المحذوف`);
        
        // إزالة الكوبونات المرتبطة بالمنتج المحذوف فقط
        const remainingCoupons = availableCoupons.filter((c: any) =>
          !couponsToRemove.includes(c.code)
        );
        
        // إرجاع المنتجات المرتبطة بالكوبونات المحذوفة إلى سعرها الأصلي
        const itemsWithResetPrices = updatedItems.map(item => {
          // التحقق إذا كان المنتج مرتبط بأحد الكوبونات المحذوفة
          const isAffectedByRemovedCoupon = couponsToRemove.some(couponCode => {
            const coupon = availableCoupons.find((c: any) => c.code === couponCode);
            if (!coupon) return false;
            
            return (coupon as any).eligibleVariants?.includes(item.variantId) ||
                   (coupon as any).eligibleProducts?.includes(item.productId) ||
                   (coupon as any).eligibleItems?.some((ei: any) =>
                     ei.variantId === item.variantId ||
                     ei.productId === item.productId
                   );
          });
          
          if (isAffectedByRemovedCoupon && item.eligibleForCoupon) {
            console.log(`↩️ إرجاع ${item.productName} للسعر الأصلي (كان مرتبط بكوبون محذوف)`);
            const originalPrice = item.originalPrice || item.price;
            return {
              ...item,
              discount: 0,
              discountType: 'fixed' as 'fixed' | 'percentage',
              total: originalPrice * item.quantity,
              eligibleForCoupon: false,
              originalPrice: undefined
            };
          }
          
          return item;
        });
        
        // تحديث البيانات
        setFormData(prev => ({
          ...prev,
          items: itemsWithResetPrices,
          availableCoupons: remainingCoupons,
          // إعادة تعيين بيانات الكوبون إذا تم حذف جميع الكوبونات
          ...(remainingCoupons.length === 0 && {
            appliedCouponDiscount: 0,
            couponCodes: [],
            couponDiscount: 0,
            buyXGetYConfig: undefined,
            discountCalculation: undefined
          })
        }));
        
        console.log(`✅ تم حذف ${couponsToRemove.length} كوبون والاحتفاظ بـ ${remainingCoupons.length} كوبون`);
        
        // إعادة حساب المجاميع
        calculateTotals(itemsWithResetPrices);
      } else {
        // لا توجد كوبونات مرتبطة بالمنتج المحذوف
        console.log(`✅ لا توجد كوبونات مرتبطة بالمنتج المحذوف`);
        
        setFormData(prev => ({
          ...prev,
          items: updatedItems
        }));
        
        calculateTotals(updatedItems);
      }
    } else {
      // لا توجد كوبونات متاحة - حذف عادي
      console.log(`✅ حذف عادي - لا توجد كوبونات متاحة`);
      
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
      
      calculateTotals(updatedItems);
    }
  };
  
  // استخراج معدل الضريبة من تصنيف المنتج
  const getTaxRateFromCategory = (categoryId: string | undefined): number => {
    if (!categoryId) return 0;
    
    // التأكد من أن taxes هو مصفوفة
    if (!Array.isArray(taxes)) {
      console.warn('taxes ليس مصفوفة:', taxes);
      return 0;
    }
    
    // العثور على التصنيف
    const category = categories.find(c => c._id === categoryId);
    if (!category || !category.taxes || !category.taxes.length) return 0;
    
    // الحصول على أول ضريبة مرتبطة بالتصنيف
    let taxId: string;
    if (typeof category.taxes[0] === 'string') {
      taxId = category.taxes[0];
    } else {
      taxId = (category.taxes[0] as ITax)._id;
    }
    
    // البحث عن معدل الضريبة
    const tax = taxes.find(t => t._id === taxId && t.isActive);
    return tax ? tax.rate : 0;
  };

  // Handle product selection in an item
  const handleProductSelect = (index: number, product: Product | null) => {
    if (!product) {
      // Clear selection
      setSelectedProductVariants(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return;
    }
    
    // Clear any previously selected variant for this index
    setSelectedProductVariants(prev => ({
      ...prev,
      [index]: null
    }));
    
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      productId: product._id,
      productName: product.name,
      price: 0,
      total: 0,
      variantId: undefined,
      barcodeId: undefined
    };
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals after changing product
    calculateTotals(updatedItems);
  };
  
  // Handle variant selection for a product
  const handleVariantSelect = async (index: number, variant: ProductVariant | null) => {
    if (!variant) return;
    
    // Store the selected variant
    setSelectedProductVariants(prev => ({
      ...prev,
      [index]: variant
    }));
    
    // Get default barcode if available
    const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
    
    // استخراج معدل الضريبة من التصنيف الافتراضي فقط
    let taxRate = 0;
    
    // البحث عن التصنيف الافتراضي للباركود الافتراضي فقط
    const defaultCategoryId = defaultBarcode?.defaultCategory;
    
    // الحصول على معدل الضريبة من التصنيف الافتراضي فقط
    if (defaultCategoryId) {
      taxRate = getTaxRateFromCategory(defaultCategoryId);
      console.log(`تم العثور على معدل ضريبة ${taxRate}% من التصنيف الافتراضي ${defaultCategoryId}`);
    } else {
      console.log(`لا يوجد تصنيف افتراضي للمتغير - معدل الضريبة = 0%`);
    }
    
    const productPrice = defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0;
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      variantId: variant._id,
      barcode: defaultBarcode?.code || '',
      barcodeId: defaultBarcode?._id || '',
      price: productPrice,
      originalPrice: updatedItems[index].originalPrice || productPrice, // حفظ السعر الأصلي إذا لم يكن موجوداً
      unit: variant.unit || '',
      unitId: '', // سيتم تعيينه من الخادم بناءً على unitName
      unitName: defaultBarcode?.unit || variant.unit || '',
      tax: taxRate, // إضافة معدل الضريبة من التصنيف
      // If warehouse is selected, get the available quantity for this variant in that warehouse
      availableQuantity: variant.warehouseInventory?.find(
        w => w.warehouseId === formData.warehouseId
      )?.availableQuantity || 0
    };
    
    // Calculate the total
    if (updatedItems[index].quantity && updatedItems[index].price) {
      const qty = updatedItems[index].quantity;
      const prc = updatedItems[index].price;
      // حساب الإجمالي الأصلي (السعر × الكمية) بدون أي خصومات
      updatedItems[index].originalTotal = qty * prc;
      updatedItems[index].total = (qty * prc) -
        (updatedItems[index].discount || 0);
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals after changing variant
    calculateTotals(updatedItems);
    
    // فحص الحملات التلقائية للمتغير المختار
    const selectedPrice = defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0;
    
    try {
      if (page?._id && variant._id && selectedPrice > 0) {
        // الحصول على اسم المنتج من العنصر المحدث
        const productName = updatedItems[index].productName || 'منتج غير معروف';
        console.log(`🔍 فحص الحملات التلقائية للمتغير المختار: ${productName}`);
        
        const promotionResult = await checkAutomaticPromotions(
          variant._id,
          page._id,
          1,
          selectedPrice
        );
        
        if (promotionResult.hasPromotions && promotionResult.coupons.length > 0) {
          console.log(`✅ تم العثور على ${promotionResult.coupons.length} كوبون تلقائي من الحملات للمتغير`);
          
          // إضافة الكوبونات الجديدة للكوبونات الموجودة (بدون تكرار)
          setFormData(prev => {
            const existingCoupons = prev.availableCoupons || [];
            const newCoupons = promotionResult.coupons.filter(
              newCoupon => !existingCoupons.some(existing => existing.code === newCoupon.code)
            ).map(coupon => ({
              code: coupon.code,
              description: coupon.description,
              promotionName: coupon.promotionName,
              discountType: coupon.discountType,
              discountValue: coupon.discountValue
            }));
            
            const updatedCoupons = [...existingCoupons, ...newCoupons];
            
            return {
              ...prev,
              marketingEnabled: true,
              availableCoupons: updatedCoupons
            };
          });
          
          console.log(`🎫 تم تجهيز ${promotionResult.coupons.length} كوبون للمتغير المختار`);
        } else {
          // لا نمسح الكوبونات إذا لم توجد حملات للمتغير الحالي
          // لأن قد تكون هناك كوبونات من منتجات أخرى
          console.log(`ℹ️ لا توجد حملات للمتغير الحالي - الاحتفاظ بالكوبونات الموجودة`);
        }
      }
    } catch (error) {
      console.error('خطأ في فحص الحملات التلقائية للمتغير المختار:', error);
    }
  };
  
  // Open attribute search dialog for a specific item index
  const openAttributeSearch = (index: number) => {
    setSelectedItemIndex(index);
    setIsProductSearchOpen(true);
  };
  
  // Handle selection from attribute search dialog
  const handleAttributeVariantSelect = async (product: Product, variant: ProductVariant) => {
    if (selectedItemIndex === -1) return;
    
    // Get default barcode if available
    const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
    
    // استخراج معدل الضريبة من التصنيف الافتراضي فقط
    let taxRate = 0;
    
    // البحث عن التصنيف الافتراضي للباركود الافتراضي فقط
    const defaultCategoryId = defaultBarcode?.defaultCategory;
    
    // الحصول على معدل الضريبة من التصنيف الافتراضي فقط
    if (defaultCategoryId) {
      taxRate = getTaxRateFromCategory(defaultCategoryId);
      console.log(`تم العثور على معدل ضريبة ${taxRate}% من التصنيف الافتراضي ${defaultCategoryId} للمنتج المختار من البحث`);
    } else {
      console.log(`لا يوجد تصنيف افتراضي للمنتج المختار من البحث - معدل الضريبة = 0%`);
    }
    
    // Update both product and variant information in one operation
    const updatedItems = [...formData.items];
    updatedItems[selectedItemIndex] = {
      ...updatedItems[selectedItemIndex],
      
      // Product info
      productId: product._id,
      productName: product.name,
      
      // Variant info
      variantId: variant._id,
      barcode: defaultBarcode?.code || '',
      barcodeId: defaultBarcode?._id || '',
      price: defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0,
      unit: variant.unit || '',
      unitId: '',
      unitName: defaultBarcode?.unit || variant.unit || '',
      attributes: variant.attributes || {},
      tax: taxRate, // إضافة معدل الضريبة من التصنيف
      
      // If warehouse is selected, get the available quantity for this variant
      availableQuantity: variant.warehouseInventory?.find(
        w => w.warehouseId === formData.warehouseId
      )?.availableQuantity || 0
    };
    
    // Calculate the total
    if (updatedItems[selectedItemIndex].quantity && updatedItems[selectedItemIndex].price) {
      updatedItems[selectedItemIndex].total = (
        updatedItems[selectedItemIndex].quantity * updatedItems[selectedItemIndex].price
      ) - (updatedItems[selectedItemIndex].discount || 0);
    }
    
    // Store the selected variant directly
    setSelectedProductVariants(prev => ({
      ...prev,
      [selectedItemIndex]: variant
    }));
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals
    calculateTotals(updatedItems);
    
    // فحص الحملات التلقائية للمنتج المختار من نافذة البحث
    const selectedPrice = defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0;
    
    try {
      if (page?._id && variant._id && selectedPrice > 0 && page.enableMarketing) {
        console.log(`🔍 فحص الحملات التلقائية للمنتج المختار من البحث: ${product.name}`);
        const promotionResult = await checkAutomaticPromotions(
          variant._id,
          page._id,
          1,
          selectedPrice
        );
        
        if (promotionResult.hasPromotions && promotionResult.coupons.length > 0) {
          console.log(`✅ تم العثور على ${promotionResult.coupons.length} كوبون تلقائي من الحملات للمنتج المختار`);
          
          // إضافة الكوبونات الجديدة للكوبونات الموجودة (بدون تكرار)
          setFormData(prev => {
            const existingCoupons = prev.availableCoupons || [];
            const newCoupons = promotionResult.coupons.filter(
              newCoupon => !existingCoupons.some(existing => existing.code === newCoupon.code)
            ).map(coupon => ({
              code: coupon.code,
              description: coupon.description,
              promotionName: coupon.promotionName,
              discountType: coupon.discountType,
              discountValue: coupon.discountValue
            }));
            
            const updatedCoupons = [...existingCoupons, ...newCoupons];
            
            return {
              ...prev,
              marketingEnabled: true,
              availableCoupons: updatedCoupons
            };
          });
          
          console.log(`🎫 تم إضافة كوبونات جديدة للمنتج المختار من البحث`);
        } else {
          // لا نمسح الكوبونات إذا لم توجد حملات للمنتج المختار
          // لأن قد تكون هناك كوبونات من منتجات أخرى
          console.log(`ℹ️ لا توجد حملات للمنتج المختار - الاحتفاظ بالكوبونات الموجودة`);
        }
      }
    } catch (error) {
      console.error('خطأ في فحص الحملات التلقائية للمنتج المختار:', error);
    }
    
    // Clear selected index
    setSelectedItemIndex(-1);
  };

  // Handle multiple product selection from search dialog
  const handleMultiProductSelect = async (items: { product: Product; variant: ProductVariant }[]) => {
    try {
      // Remove empty rows (items without productId) before adding new products
      const cleanedItems = formData.items.filter(item => item.productId && item.productId.trim() !== '');
      const updatedItems = [...cleanedItems];
      
      for (const item of items) {
        const { product, variant } = item;
        const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
        
        // استخراج معدل الضريبة من التصنيف الافتراضي فقط
        let taxRate = 0;
        const defaultCategoryId = defaultBarcode?.defaultCategory;
        
        if (defaultCategoryId) {
          taxRate = getTaxRateFromCategory(defaultCategoryId);
        }
        
        const newItem: IInvoiceItemExtended = {
          productId: product._id,
          productName: product.name,
          variantId: variant._id,
          barcode: defaultBarcode?.code || '',
          barcodeId: defaultBarcode?._id || '',
          price: defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0,
          quantity: 1,
          discount: 0,
          discountType: 'fixed',
          tax: taxRate,
          total: defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0,
          unit: variant.unit || '',
          unitId: '',
          unitName: defaultBarcode?.unit || variant.unit || '',
          attributes: variant.attributes || {},
          availableQuantity: variant.warehouseInventory?.find(
            w => w.warehouseId === formData.warehouseId
          )?.availableQuantity || 0,
          isServiceProduct: false
        };
        
        updatedItems.push(newItem);
      }
      
      // Update selectedProductVariants for new items only (preserve existing ones)
      const newVariantMap: Record<number, ProductVariant> = {};
      items.forEach((item, idx) => {
        const realIndex = cleanedItems.length + idx;
        newVariantMap[realIndex] = item.variant;
      });
      
      setSelectedProductVariants(prev => ({
        ...prev,
        ...newVariantMap
      }));
      
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
      
      calculateTotals(updatedItems);
      
      // فحص الحملات التلقائية للمنتجات المختارة
      for (const item of items) {
        const defaultBarcode = item.variant.barcodes.find(b => b.isDefault) || item.variant.barcodes[0];
        const selectedPrice = defaultBarcode ? getPriceByType(defaultBarcode, page?.defaultPriceType || 'retailPrice') : 0;
        
        try {
          if (page?._id && item.variant._id && selectedPrice > 0 && page.enableMarketing) {
            const promotionResult = await checkAutomaticPromotions(
              item.variant._id,
              page._id,
              1,
              selectedPrice
            );
            
            if (promotionResult.hasPromotions && promotionResult.coupons.length > 0) {
              setFormData(prev => {
                const existingCoupons = prev.availableCoupons || [];
                const newCoupons = promotionResult.coupons.filter(
                  newCoupon => !existingCoupons.some(existing => existing.code === newCoupon.code)
                ).map(coupon => ({
                  code: coupon.code,
                  description: coupon.description,
                  promotionName: coupon.promotionName,
                  discountType: coupon.discountType,
                  discountValue: coupon.discountValue
                }));
                
                const updatedCoupons = [...existingCoupons, ...newCoupons];
                
                return {
                  ...prev,
                  marketingEnabled: true,
                  availableCoupons: updatedCoupons
                };
              });
            }
          }
        } catch (error) {
          console.error('خطأ في فحص الحملات التلقائية للمنتج المختار:', error);
        }
      }
      
      setBarcodeMessage(`تم إضافة ${items.length} منتج بنجاح`);
      setShowBarcodeMessage(true);
      setIsProductSearchOpen(false);
    } catch (error) {
      console.error('Error adding multiple products:', error);
      setBarcodeMessage('حدث خطأ أثناء إضافة المنتجات');
      setShowBarcodeMessage(true);
    }
  };
  
  // Handle barcode selection for a variant
  const handleBarcodeSelect = (index: number, barcodeId: string) => {
    const variant = selectedProductVariants[index];
    if (!variant) return;
    
    const barcode = variant.barcodes.find(b => b._id === barcodeId);
    if (!barcode) return;
    
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      barcodeId: barcode._id,
      barcode: barcode.code,
      price: getPriceByType(barcode, page?.defaultPriceType || 'retailPrice'),
    };
    
    // Calculate the total
    if (updatedItems[index].quantity && updatedItems[index].price) {
      const qty = updatedItems[index].quantity;
      const prc = updatedItems[index].price;
      // حساب الإجمالي الأصلي (السعر × الكمية) بدون أي خصومات
      updatedItems[index].originalTotal = qty * prc;
      updatedItems[index].total = (qty * prc) -
        (updatedItems[index].discount || 0);
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals after changing barcode
    calculateTotals(updatedItems);
  };
  
  // Handle item property change
  const handleItemChange = async (
    index: number,
    field: keyof IInvoiceItemExtended,
    value: string | number
  ) => {
    const updatedItems = [...formData.items];
    
    // إذا كان التغيير في السعر وليس هناك سعر أصلي محفوظ، احفظ السعر الحالي كسعر أصلي
    if (field === 'price' && !updatedItems[index].originalPrice) {
      updatedItems[index].originalPrice = updatedItems[index].price;
    }
    
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    // إعادة تعيين حالة التحميل للتعديل عند تعديل أي منتج
    setIsLoadedForEdit(false);
    
    // Calculate item total
    if (field === 'price' || field === 'quantity' || field === 'discount' || field === 'discountType' || field === 'tax') {
      const price = updatedItems[index].price || 0;
      const quantity = updatedItems[index].quantity || 0;
      const discount = updatedItems[index].discount || 0;
      const discountType = updatedItems[index].discountType || 'fixed';
      const tax = updatedItems[index].tax || 0;
      
      // First calculate price * quantity
      const subtotal = price * quantity;
      
      // حساب الإجمالي الأصلي (السعر × الكمية) بدون أي خصومات
      updatedItems[index].originalTotal = subtotal;
      
      // Calculate discount amount based on discount type
      let discountAmount = 0;
      if (discountType === 'percentage') {
        // حساب الخصم كنسبة مئوية من السعر الإجمالي
        discountAmount = subtotal * (discount / 100);
      } else {
        // الخصم كمبلغ ثابت
        discountAmount = discount;
      }
      
      // Apply discount to get the final total
      updatedItems[index].total = Math.max(0, subtotal - discountAmount);
      
      // 🔥 إضافة: فحص الحملات التلقائية عند تغيير الكمية مع debounce
      if (field === 'quantity' && updatedItems[index].variantId && page?._id) {
        // حفظ pageId في متغير محلي لاستخدامه داخل setTimeout
        const currentPageId = page._id;
        
        // إلغاء الطلب السابق إذا كان موجوداً (debounce)
        if (promotionCheckTimeout) {
          clearTimeout(promotionCheckTimeout);
          console.log('⏸️ تم إلغاء فحص الحملات السابق - انتظار توقف التغييرات');
        }
        
        // تأخير فحص الحملات لمدة 400ms بعد آخر تغيير
        const timeout = setTimeout(async () => {
          try {
            console.log(`🔍 فحص الحملات التلقائية بعد تغيير الكمية للمنتج: ${updatedItems[index].productName}`);
            const promotionResult = await checkAutomaticPromotions(
              updatedItems[index].variantId!,
              currentPageId,
              Number(value),
              price
            );
            
            if (promotionResult.hasPromotions && promotionResult.coupons.length > 0) {
              console.log(`✅ تم العثور على ${promotionResult.coupons.length} كوبون تلقائي بعد تغيير الكمية`);
              
              // إضافة الكوبونات الجديدة للكوبونات الموجودة (بدون تكرار)
              setFormData(prev => {
                const existingCoupons = prev.availableCoupons || [];
                const newCoupons = promotionResult.coupons.filter(
                  newCoupon => !existingCoupons.some(existing => existing.code === newCoupon.code)
                ).map(coupon => ({
                  code: coupon.code,
                  description: coupon.description,
                  promotionName: coupon.promotionName,
                  discountType: coupon.discountType,
                  discountValue: coupon.discountValue
                }));
                
                const updatedCoupons = [...existingCoupons, ...newCoupons];
                
                return {
                  ...prev,
                  marketingEnabled: true,
                  availableCoupons: updatedCoupons
                };
              });
              
              console.log(`🎫 تم تجهيز ${promotionResult.coupons.length} كوبون بعد تغيير الكمية`);
            }
          } catch (error) {
            console.error('خطأ في فحص الحملات التلقائية بعد تغيير الكمية:', error);
          }
        }, 400); // انتظار 400ms بعد آخر تغيير
        
        setPromotionCheckTimeout(timeout);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals after changing an item
    calculateTotals(updatedItems);
  };
  
  // معالج تغيير طرق الدفع
  const handlePaymentMethodsChange = (paymentMethods: IPaymentMethodUsage[]) => {
    console.log('تحديث طرق الدفع:', paymentMethods);
    setFormData(prev => ({
      ...prev,
      paymentMethods
    }));
  };
  // معالجة استبدال نقاط الولاء (تخزين مؤقت - سيتم الخصم الفعلي عند الحفظ)
  const handleLoyaltyPointsRedeem = (points: number, discountAmount: number) => {
    console.log(`🎁 تطبيق خصم ${discountAmount} ريال من ${points} نقطة (سيتم الخصم عند الحفظ)`);
    console.log(`🎁 القيم المستلمة - points: ${points}, discountAmount: ${discountAmount}`);
    
    // تطبيق الخصم على الفاتورة وحفظ معلومات النقاط
    setFormData(prev => {
      const currentDiscount = prev.discount || 0;
      const newDiscount = currentDiscount + discountAmount;
      
      const updatedData = {
        ...prev,
        discount: newDiscount,
        discountType: 'fixed' as 'fixed' | 'percentage',
        loyaltyPointsRedeemed: points,
        loyaltyDiscountAmount: discountAmount
      };
      
      console.log(`🎁 تحديث formData بنقاط الولاء:`, {
        loyaltyPointsRedeemed: updatedData.loyaltyPointsRedeemed,
        loyaltyDiscountAmount: updatedData.loyaltyDiscountAmount,
        discount: updatedData.discount
      });
      
      return updatedData;
    });
    
    // إعادة حساب المجاميع
    calculateTotals(formData.items, (formData.discount || 0) + discountAmount, 'fixed');
  };
  
  
  /**
   * معالجة تغييرات قسم التسويق
   * تطبيق الخصومات المحسوبة من couponManager (الخادم) على المنتجات لعرضها في الجدول
   * ملاحظة: جميع الخصومات محسوبة في الخادم - هذا الكود فقط يعرضها
   */
  const handleMarketingChange = (marketingData: {
    marketingEnabled: boolean;
    appliedCoupons?: any[];
    totalDiscount?: number;
    removedCoupons?: string[];
  }) => {
    console.log('🔍 [InvoiceFormPage] تم تحديث بيانات التسويق من couponManager:', marketingData);
    console.log('📊 إجمالي الخصم من الكوبونات:', marketingData.totalDiscount);
    console.log('🎫 عدد الكوبونات المطبقة:', marketingData.appliedCoupons?.length || 0);
    
    // إذا كانت هناك كوبونات محذوفة، احذفها من availableCoupons
    if (marketingData.removedCoupons && marketingData.removedCoupons.length > 0) {
      console.log(`🗑️ [InvoiceFormPage] حذف ${marketingData.removedCoupons.length} كوبون من availableCoupons:`, marketingData.removedCoupons);
      
      setFormData(prev => ({
        ...prev,
        availableCoupons: (prev.availableCoupons || []).filter(
          (c: any) => !marketingData.removedCoupons!.includes(c.code)
        )
      }));
    }
    
    // تطبيق الخصومات على المنتجات الفردية (للعرض فقط - الخصومات محسوبة في الخادم)
    if (marketingData.appliedCoupons && Array.isArray(marketingData.appliedCoupons) && marketingData.appliedCoupons.length > 0) {
      console.log('🎯 تطبيق خصومات الكوبونات على المنتجات...');
      
      const updatedItems = formData.items.map(item => {
        // حفظ السعر الأصلي إذا لم يكن محفوظاً
        if (!item.originalPrice) {
          item.originalPrice = item.price;
        }
        
        // التحقق من أهلية المنتج لأي كوبون
        let itemDiscount = 0;
        let isEligible = false;
        
        // التأكد من وجود appliedCoupons قبل المرور عليها
        const coupons = marketingData.appliedCoupons || [];
        for (const coupon of coupons) {
          // التحقق من أهلية المنتج للكوبون
          // الأولوية للتحقق من variantId ثم productId
          const isVariantEligible = coupon.eligibleVariants?.includes(item.variantId);
          
          // التحقق من eligibleItems (الأدق - يحتوي على variantId)
          const eligibleItem = coupon.eligibleItems?.find((ei: any) =>
            ei.variantId === item.variantId
          );
          
          // التحقق من productId فقط إذا لم يكن هناك variantId محدد في الكوبون
          const isProductEligible = !coupon.eligibleVariants?.length &&
                                   !coupon.eligibleItems?.length &&
                                   coupon.eligibleProducts?.includes(item.productId);
          
          if (isVariantEligible || eligibleItem || isProductEligible) {
            isEligible = true;
            
            // حساب الخصم بناءً على نوع الكوبون
            if (coupon.discountCalculation?.eligibleItems) {
              // استخدام الخصم المحسوب من الخادم
              const serverCalcItem = coupon.discountCalculation.eligibleItems.find((ei: any) =>
                (ei.productId === item.productId || ei.variantId === item.variantId)
              );
              
              if (serverCalcItem && serverCalcItem.discountPerUnit) {
                // ⚠️ حملة X و Y فقط: الخصم ثابت ولا يتضاعف مع الكمية
                if (coupon.discountCalculation?.schemeType === 'buy_x_get_y' &&
                    serverCalcItem.itemDiscount !== undefined &&
                    serverCalcItem.itemDiscount !== null) {
                  itemDiscount += serverCalcItem.itemDiscount;
                  console.log(`✅ [X&Y] تطبيق خصم إجمالي ثابت ${serverCalcItem.itemDiscount} على ${item.productName}`);
                } else {
                  // جميع الحملات الأخرى (كومبو، خصم منتج، إلخ): نضرب في الكمية
                  itemDiscount += serverCalcItem.discountPerUnit * item.quantity;
                  console.log(`✅ تطبيق خصم ${serverCalcItem.discountPerUnit} × ${item.quantity} على ${item.productName}`);
                }
              }
            } else if (coupon.discountType === 'percentage') {
              // خصم نسبة مئوية
              const itemTotal = (item.originalPrice || item.price) * item.quantity;
              itemDiscount += itemTotal * (coupon.discount / 100);
              console.log(`✅ تطبيق خصم ${coupon.discount}% على ${item.productName}`);
            } else {
              // خصم ثابت - يتم توزيعه على المنتجات المؤهلة
              // هذا يحتاج حساب أكثر تعقيداً، لذا نستخدم الخصم من الخادم
              if (eligibleItem && eligibleItem.discountPerUnit) {
                // ⚠️ حملة X و Y فقط: الخصم ثابت ولا يتضاعف مع الكمية
                if (coupon.schemeType === 'buy_x_get_y' &&
                    eligibleItem.itemDiscount !== undefined &&
                    eligibleItem.itemDiscount !== null) {
                  itemDiscount += eligibleItem.itemDiscount;
                  console.log(`✅ [X&Y] تطبيق خصم إجمالي ثابت ${eligibleItem.itemDiscount} على ${item.productName}`);
                } else {
                  // جميع الحملات الأخرى: نضرب في الكمية
                  itemDiscount += eligibleItem.discountPerUnit * item.quantity;
                  console.log(`✅ تطبيق خصم ${eligibleItem.discountPerUnit} × ${item.quantity} على ${item.productName}`);
                }
              }
            }
          }
        }
        
        // تطبيق الخصم على المنتج
        if (isEligible && itemDiscount > 0) {
          return {
            ...item,
            discount: itemDiscount,
            discountType: 'fixed' as 'fixed' | 'percentage',
            eligibleForCoupon: true,
            total: Math.max(0, (item.originalPrice || item.price) * item.quantity - itemDiscount)
          };
        }
        
        // إذا لم يكن مؤهلاً، إرجاع السعر الأصلي
        return {
          ...item,
          discount: 0,
          discountType: 'fixed' as 'fixed' | 'percentage',
          eligibleForCoupon: false,
          total: (item.originalPrice || item.price) * item.quantity
        };
      });
      
      console.log('✅ تم تطبيق الخصومات على المنتجات');
      
      // تحديث المنتجات والحالة
     setFormData(prev => ({
       ...prev,
       items: updatedItems,
       marketingEnabled: marketingData.marketingEnabled,
       appliedCouponDiscount: marketingData.totalDiscount || 0,
       // ✅ تحديث أكواد الكوبونات بناءً على الكوبونات المطبقة
       couponCodes: marketingData.appliedCoupons && marketingData.appliedCoupons.length > 0
         ? marketingData.appliedCoupons.map(c => c.code)
         : []
     }));
     
     // إعادة حساب المجاميع
     calculateTotals(updatedItems);
    } else {
      // إذا لم توجد كوبونات، إزالة الخصومات من المنتجات
      console.log('🧹 إزالة خصومات الكوبونات من المنتجات...');
      
      const updatedItems = formData.items.map(item => ({
        ...item,
        discount: 0,
        discountType: 'fixed' as 'fixed' | 'percentage',
        eligibleForCoupon: false,
        total: (item.originalPrice || item.price) * item.quantity
      }));
      
      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        marketingEnabled: marketingData.marketingEnabled,
        appliedCouponDiscount: 0,
        // ✅ مسح أكواد الكوبونات عند عدم وجود كوبونات مطبقة
        couponCodes: []
      }));
      
      // إعادة حساب المجاميع
      calculateTotals(updatedItems);
    }
  };
  
  // Calculate invoice totals - تم تبسيطها بالكامل لإزالة منطق الكوبونات المحلي
  // الآن تعتمد فقط على appliedCouponDiscount من couponManager
  const calculateTotals = (
    items: IInvoiceItemExtended[],
    currentDiscount?: number,
    currentDiscountType?: 'fixed' | 'percentage',
    skipRecalculation?: boolean
  ) => {
    const effectiveDiscount = currentDiscount ?? formData.discount ?? 0;
    const effectiveDiscountType = currentDiscountType ?? formData.discountType ?? 'fixed';
    console.log(`🧮 حساب المجاميع: الخصم=${effectiveDiscount}, النوع=${effectiveDiscountType}`);
    
    // إذا كان skipRecalculation = true، نحسب المجاميع فقط بدون تعديل خصومات المنتجات
    if (skipRecalculation) {
      console.log('تجاهل إعادة حساب خصومات المنتجات - استخدام القيم الموجودة');
      
      // حساب المجاميع مباشرة من القيم الموجودة
      const rawSubtotal = items.reduce((total, item) => total + (item.total || 0), 0);
      
      let totalBeforeTax = 0;
      let totalTaxAmount = 0;
      
      items.forEach(item => {
        if (!item.tax) {
          totalBeforeTax += (item.total || 0);
        } else {
          const taxRate = item.tax / 100;
          const itemBeforeTax = (item.total || 0) / (1 + taxRate);
          const itemTax = (item.total || 0) - itemBeforeTax;
          
          totalBeforeTax += itemBeforeTax;
          totalTaxAmount += itemTax;
        }
      });
      
      // تدوير القيم إلى رقمين عشريين
      totalBeforeTax = Math.round(totalBeforeTax * 100) / 100;
      totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;
      
      // تحديث النموذج بالقيم المحسوبة
      setFormData(prev => ({
        ...prev,
        subtotalBeforeTax: totalBeforeTax,
        subtotal: rawSubtotal,
        tax: totalTaxAmount,
        total: rawSubtotal
      }));
      
      console.log('تم حساب المجاميع بدون تعديل خصومات المنتجات:', {
        subtotalBeforeTax: totalBeforeTax,
        subtotal: rawSubtotal,
        tax: totalTaxAmount,
        total: rawSubtotal
      });
      
      return;
    }
    
    // 1. حساب المجموع الإجمالي للمنتجات (شامل الضريبة) قبل تطبيق الخصم
    const rawSubtotal = items.reduce((total, item) => total + (item.total || 0), 0);
    const rawSubtotalNonService = items.reduce((total, item) =>
      item.isServiceProduct ? total : total + (item.total || 0),
    0);

    // 2. حساب قيمة الخصم الكلي للفاتورة
    let discountAmount = 0;
    let discountFactor = 1;

    if (effectiveDiscountType === 'percentage') {
      // إذا كان الخصم نسبة مئوية - نحسبه فقط على المنتجات غير الخدمية
      const baseForDiscount = rawSubtotalNonService > 0 ? rawSubtotalNonService : rawSubtotal;
      discountAmount = baseForDiscount * (effectiveDiscount / 100);
      discountFactor = 1 - (effectiveDiscount / 100);
    } else {
      // إذا كان الخصم مبلغاً ثابتاً
      discountAmount = effectiveDiscount;
      const baseForDiscount = rawSubtotalNonService > 0 ? rawSubtotalNonService : rawSubtotal;
      if (baseForDiscount > 0) {
        discountFactor = Math.max(0, 1 - (effectiveDiscount / baseForDiscount));
      }
    }

    // 3. حساب المجموع بعد تطبيق الخصم
    const subtotal = Math.max(0, rawSubtotal - discountAmount);

    // 5. حساب المجموع قبل الضريبة وقيمة الضريبة بعد تطبيق الخصم
    let totalBeforeTax = 0;
    let totalTaxAmount = 0;

    // لكل منتج، نحسب قيمته بعد تطبيق الخصم النسبي ثم نستخرج الضريبة
    items.forEach(item => {
      if (item.isServiceProduct) {
        // المنتجات الخدمية لا تتأثر بالخصم الكلي
        if (!item.tax) {
          totalBeforeTax += (item.total || 0);
        } else {
          const taxRate = item.tax / 100;
          const itemBeforeTax = (item.total || 0) / (1 + taxRate);
          const itemTax = (item.total || 0) - itemBeforeTax;
          totalBeforeTax += itemBeforeTax;
          totalTaxAmount += itemTax;
        }
      } else {
        // المنتجات غير الخدمية تتأثر بالخصم
        if (!item.tax) {
          totalBeforeTax += (item.total || 0) * discountFactor;
        } else {
          const itemValueAfterAllDiscounts = (item.total || 0) * discountFactor;
          const taxRate = item.tax / 100;
          const itemBeforeTax = itemValueAfterAllDiscounts / (1 + taxRate);
          const itemTax = itemValueAfterAllDiscounts - itemBeforeTax;
          totalBeforeTax += itemBeforeTax;
          totalTaxAmount += itemTax;
        }
      }
    });

    // تدوير القيم إلى رقمين عشريين لتجنب أخطاء التقريب
    totalBeforeTax = Math.round(totalBeforeTax * 100) / 100;
    totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;

    // 6. الإجمالي النهائي = subtotal (خصم الكوبون مطبق بالفعل على المنتجات الفردية)
    // ملاحظة: لا نطرح appliedCouponDiscount هنا لأنه مطبق بالفعل على item.total
    const finalTotal = subtotal;

    console.log(`📊 النتيجة النهائية:`);
    console.log(`   - المجموع الفرعي: ${subtotal.toFixed(2)} ريال`);
    console.log(`   - الضريبة: ${totalTaxAmount.toFixed(2)} ريال`);
    console.log(`   - الإجمالي النهائي: ${finalTotal.toFixed(2)} ريال`);

    // 8. تحديث حالة النموذج
    setFormData(prev => ({
      ...prev,
      subtotalBeforeTax: totalBeforeTax,
      subtotal: subtotal,
      tax: totalTaxAmount,
      total: finalTotal
    }));
  };
  
  // Submit form
  const handleSubmit = async () => {
    // Reset error state
    setError(null);
    
    // التحقق من وجود فرع نشط للمستخدم
    if (!activeBranch) {
      setError('يجب تحديد فرع نشط قبل حفظ الفاتورة');
      requireBranchSelection(true);
      return;
    }
    
    // التحقق من أن الفاتورة تستخدم الفرع النشط الحالي فقط
    if (isEditing) {
      // في حالة التعديل، التأكد من أن الفاتورة تنتمي للفرع النشط الحالي
      if (formData.branchId !== activeBranch._id) {
        setError('لا يمكن تعديل هذه الفاتورة لأنها تنتمي لفرع مختلف عن الفرع النشط الحالي');
        return;
      }
    } else {
      // في حالة إنشاء فاتورة جديدة، استخدام الفرع النشط دائمًا
      setFormData(prev => ({
        ...prev,
        branchId: activeBranch._id
      }));
    }
    
    // Validation
    if (!formData.pageId) {
      setError('معرف صفحة الفاتورة مطلوب');
      return;
    }
    
    if (!formData.items || formData.items.length === 0) {
      setError('يرجى إضافة منتج واحد على الأقل');
      return;
    }
    
    if (page && ['sales_invoice', 'sales_return'].includes(page.type) && !formData.customerId) {
      setError('يرجى اختيار عميل');
      return;
    }
    
    if (page && ['purchase_invoice', 'purchase_return'].includes(page.type) && !formData.supplierId) {
      setError('يرجى اختيار مورد');
      return;
    }
    
    // Validate items have the necessary data
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!item.productId || !item.productName) {
        setError(`المنتج رقم ${i+1} غير مكتمل. يرجى اختيار المنتج`);
        return;
      }
      // السماح بالقيم السالبة/صفر فقط لفواتير بضاعة أول المدة
      if (page?.type !== 'beginning_inventory' && (!item.quantity || item.quantity <= 0)) {
        setError(`الكمية للمنتج رقم ${i+1} غير صحيحة`);
        return;
      }
      if (item.price < 0) {
        setError(`السعر للمنتج رقم ${i+1} غير صحيح`);
        return;
      }
    }
    
    // التحقق من طرق الدفع إذا كانت مفعلة
    if (page && page.enablePaymentMethods) {
      console.log('🔍 التحقق من طرق الدفع - page.enablePaymentMethods:', page.enablePaymentMethods);
      
      // التحقق من وجود بيانات غير محفوظة في نموذج طرق الدفع
      const unsavedCheck = (window as any).__paymentMethodUnsavedCheck?.();
      console.log('🔍 نتيجة التحقق من البيانات غير المحفوظة:', unsavedCheck);
      
      if (unsavedCheck && unsavedCheck.hasUnsavedData) {
        console.log('⚠️ تم اكتشاف بيانات غير محفوظة!');
        const confirmSave = window.confirm(
          `⚠️ تحذير: لم تضغط على زر "إضافة"!\n\n` +
          `لديك بيانات غير محفوظة في نموذج طرق الدفع:\n` +
          `• المبلغ: ${unsavedCheck.amount.toFixed(2)} ريال\n\n` +
          `هل تريد حفظ الفاتورة بدون إضافة هذه الطريقة؟`
        );
        
        if (!confirmSave) {
          console.log('❌ المستخدم ألغى عملية الحفظ');
          return; // إلغاء عملية الحفظ
        }
        console.log('✅ المستخدم وافق على المتابعة');
      } else {
        console.log('✅ لا توجد بيانات غير محفوظة');
      }
    }
    
    // إعادة حساب المجاميع قبل الإرسال للتأكد من أن البيانات المرسلة محدثة
    console.log('إعادة حساب المجاميع قبل إرسال البيانات إلى الخادم');
    
    // حساب المجاميع والقيم النهائية
    const currentItems = [...formData.items];
    const currentDiscount = formData.discount || 0;
    const currentDiscountType = formData.discountType || 'fixed';
    
    // 1. حساب المجموع الإجمالي للمنتجات (شامل الضريبة) قبل تطبيق الخصم
    const rawSubtotal = currentItems.reduce((total, item) => total + (item.total || 0), 0);
    
    // 2. حساب قيمة الخصم الكلي للفاتورة
    let discountAmount = 0;
    if (currentDiscountType === 'percentage') {
      discountAmount = rawSubtotal * (currentDiscount / 100);
    } else {
      discountAmount = currentDiscount;
    }
    
    // 3. حساب المجموع بعد تطبيق الخصم
    const subtotal = Math.max(0, rawSubtotal - discountAmount);
    
    // 4. حساب معامل توزيع الخصم على المنتجات
    const discountFactor = rawSubtotal > 0 ? subtotal / rawSubtotal : 1;
    
    // 5. حساب المجموع قبل الضريبة وقيمة الضريبة بعد تطبيق الخصم
    let totalBeforeTax = 0;
    let totalTaxAmount = 0;
    
    // لكل منتج، نحسب قيمته بعد تطبيق الخصم النسبي ثم نستخرج الضريبة
    // في هذه النسخة نحتفظ بالمعلومات المحسوبة للمجاميع فقط دون تغيير كائنات المنتجات
    currentItems.forEach(item => {
      // حساب القيمة بعد توزيع الخصم الإجمالي
      const itemValueAfterAllDiscounts = (item.total || 0) * discountFactor;
      
      // حساب قيمة الضريبة والقيمة قبل الضريبة
      if (item.tax) {
        const taxRate = item.tax / 100;
        const itemBeforeTax = itemValueAfterAllDiscounts / (1 + taxRate);
        const itemTax = itemValueAfterAllDiscounts - itemBeforeTax;
        
        totalBeforeTax += itemBeforeTax;
        totalTaxAmount += itemTax;
      } else {
        totalBeforeTax += itemValueAfterAllDiscounts;
      }
    });
    
      // تدوير القيم إلى رقمين عشريين لتجنب أخطاء التقريب
      totalBeforeTax = Math.round(totalBeforeTax * 100) / 100;
      totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;
      
      // جلب رقم هاتف العميل من بيانات العميل المحدد
      let customerPhone: string | undefined = undefined;
      if (formData.customerId && customers.length > 0) {
        const selectedCustomer = customers.find(c => c._id === formData.customerId);
        if (selectedCustomer && selectedCustomer.phone) {
          customerPhone = selectedCustomer.phone;
          console.log('📞 رقم هاتف العميل:', customerPhone);
        }
      }
      
      // جلب رقم هاتف المورد من بيانات المورد المحدد
      let supplierPhone: string | undefined = undefined;
      if (formData.supplierId && suppliers.length > 0) {
        const selectedSupplier = suppliers.find(s => s._id === formData.supplierId);
        if (selectedSupplier && selectedSupplier.phone) {
          supplierPhone = selectedSupplier.phone;
          console.log('📞 رقم هاتف المورد:', supplierPhone);
        }
      }
      
      // إنشاء نسخة محدثة من بيانات الفاتورة مع القيم المحسوبة
      const updatedFormData = {
        ...formData,
        subtotalBeforeTax: totalBeforeTax,
        subtotal: subtotal,
        tax: totalTaxAmount,
        total: subtotal,
        discount: currentDiscount,
        // التأكد من إرسال نوع الخصم بشكل صريح - هذا مهم جداً
        discountType: currentDiscountType,
        // إضافة أكواد الكوبونات - دعم مصفوعة من أكواد الكوبونات
        couponCodes: formData.couponCodes || [],
        // إضافة أرقام الهواتف للإشعارات
        customerPhone: customerPhone,
        supplierPhone: supplierPhone,
        // إضافة معلومات نقاط الولاء المستبدلة
        loyaltyPointsRedeemed: (formData as any).loyaltyPointsRedeemed,
        loyaltyDiscountAmount: (formData as any).loyaltyDiscountAmount,
        // إضافة المبلغ المدفوع للدفع الجزئي
        cashPaidAmount: page?.enablePartialPayment ? paidAmount : undefined,
        isPartialPayment: page?.enablePartialPayment && paidAmount > 0 && paidAmount < subtotal
      };
    
    console.log('البيانات المحدثة قبل الإرسال:', {
      subtotalBeforeTax: totalBeforeTax,
      subtotal: subtotal,
      tax: totalTaxAmount,
      total: subtotal,
      discount: currentDiscount,
      discountType: currentDiscountType,
      // إضافة أكواد الكوبونات للعرض في الـ log
      couponCodes: formData.couponCodes || [],
      // عرض النوع الفعلي للتأكد من أنه قيمة وليس نصاً
      discountTypeActual: typeof currentDiscountType,
      isPercentage: currentDiscountType === 'percentage',
      // معلومات نقاط الولاء
      loyaltyPointsRedeemed: (formData as any).loyaltyPointsRedeemed,
      loyaltyDiscountAmount: (formData as any).loyaltyDiscountAmount
    });
    
    console.log('🎁 نقاط الولاء في updatedFormData:', {
      loyaltyPointsRedeemed: updatedFormData.loyaltyPointsRedeemed,
      loyaltyDiscountAmount: updatedFormData.loyaltyDiscountAmount
    });
    
    setSaving(true);
    
    try {
      // Ensure payment methods are included in the data
      console.log('إرسال طرق الدفع:', updatedFormData.paymentMethods);
      
      // إرسال البيانات المحدثة إلى الخادم
      let invoiceResponse;
      if (isEditing && invoiceId) {
        invoiceResponse = await invoiceService.updateInvoice(invoiceId, updatedFormData);
        setSavedInvoiceId(invoiceId);
        setSavedInvoiceNumber(invoiceResponse.invoiceNumber || formData.invoiceNumber);
      } else {
        invoiceResponse = await invoiceService.createInvoice(updatedFormData);
        setSavedInvoiceId(invoiceResponse._id);
        setSavedInvoiceNumber(invoiceResponse.invoiceNumber || formData.invoiceNumber);
      }
      
      // ملاحظة: إنشاء عمليات خدمة المنتج يتم الآن في الخادم تلقائياً
      console.log('تم حفظ الفاتورة، سيقوم الخادم بإنشاء عمليات خدمة المنتج تلقائياً');
      
      // تحديث حالة طلب WooCommerce إذا كانت الفاتورة من WooCommerce
      if (wooCommerceData?.fromWooCommerce && wooCommerceData?.wooOrderId) {
        try {
          console.log('🛒 تحديث حالة طلب WooCommerce:', wooCommerceData.wooOrderId);
          const token = localStorage.getItem('token');
          await axios.post(
            `${API_BASE_URL}/woocommerce/orders/${wooCommerceData.wooOrderId}/mark-converted`,
            { invoiceId: invoiceResponse._id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ تم تحديث حالة طلب WooCommerce بنجاح');
        } catch (wooErr: any) {
          console.error('⚠️ خطأ في تحديث حالة طلب WooCommerce:', wooErr);
          // لا نوقف العملية - الفاتورة محفوظة بنجاح
        }
      }
      
      // خصم نقاط الولاء إذا تم استخدامها
      if (updatedFormData.loyaltyPointsRedeemed && updatedFormData.loyaltyPointsRedeemed > 0 && updatedFormData.customerId) {
        try {
          console.log(`🎁 خصم ${updatedFormData.loyaltyPointsRedeemed} نقطة ولاء للعميل`);
          await axiosInstance.post('/customers/redeem-loyalty-points', {
            customerId: updatedFormData.customerId,
            points: updatedFormData.loyaltyPointsRedeemed,
            discountAmount: updatedFormData.loyaltyDiscountAmount || 0,
            invoiceId: invoiceResponse._id,
            invoiceNumber: invoiceResponse.invoiceNumber
          });
          console.log('✅ تم خصم نقاط الولاء بنجاح');
        } catch (loyaltyErr: any) {
          console.error('⚠️ خطأ في خصم نقاط الولاء:', loyaltyErr);
          console.error('⚠️ تفاصيل الخطأ:', loyaltyErr.response?.data);
          // لا نوقف العملية - الفاتورة محفوظة بنجاح
          alert('تم حفظ الفاتورة بنجاح ولكن حدث خطأ في خصم نقاط الولاء. يرجى التحقق من رصيد العميل.');
        }
      }
      
      // تفعيل زر الطباعة وحفظ معرف الفاتورة
      setShowPrintButton(true);
      
      // إظهار نافذة النجاح مع الخيارات
      setShowSuccessDialog(true);
    } catch (err: any) {
      console.error('Error saving invoice:', err);
      setError(err.message || 'حدث خطأ أثناء حفظ الفاتورة. يرجى المحاولة مرة أخرى.');
      setSaving(false);
      setShowSuccessDialog(false); // إخفاء نافذة النجاح في حالة الخطأ
    }
  };
  // طباعة الفاتورة - يستخدم القالب الافتراضي أو الأول في القائمة المعدة مسبقاً من إعدادات الصفحة
  const handlePrintInvoice = async () => {
    if (!savedInvoiceId) return;
    
    try {
      // استخدام القالب الافتراضي المعين مسبقاً في صفحة إعدادات الفواتير
      const pageData = await invoiceService.getInvoicePageById(formData.pageId);
      
      if (pageData.defaultPrintTemplate) {
        // إذا كان هناك قالب افتراضي، افتح نافذة الطباعة مباشرة باستخدام القالب المحدد
        window.open(`/api/invoices/${savedInvoiceId}/print?templateId=${pageData.defaultPrintTemplate}`, '_blank');
      } else if (pageData.printTemplates && pageData.printTemplates.length > 0) {
        // إذا لم يكن هناك قالب افتراضي لكن هناك قوالب معينة، استخدم القالب الأول
        window.open(`/api/invoices/${savedInvoiceId}/print?templateId=${pageData.printTemplates[0]}`, '_blank');
      } else {
        // إذا لم يكن هناك أي قوالب، استخدم وظيفة الطباعة الافتراضية
        setBarcodeMessage('لم يتم تعيين قوالب طباعة لهذه الصفحة. يرجى تعيين قوالب في إعدادات الصفحة.');
        setShowBarcodeMessage(true);
        invoiceService.printInvoice(savedInvoiceId);
      }
    } catch (err) {
      console.error('Error getting print templates:', err);
      // في حالة حدوث خطأ، استخدم وظيفة الطباعة الافتراضية
      invoiceService.printInvoice(savedInvoiceId);
    }
  };
  
  // استيراد المنتجات من ملف إكسل - محسّن للملفات الكبيرة
  const handleExcelImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // التحقق من تفعيل ميزة استيراد الإكسل
    if (!page?.enableExcelImport) {
      setBarcodeMessage('ميزة استيراد الإكسل غير مفعلة في إعدادات الصفحة');
      setShowBarcodeMessage(true);
      return;
    }
    
    setImportLoading(true);
    
    try {
      // قراءة ملف الإكسل
      console.log('📂 بدء قراءة ملف Excel...');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // استخدام الورقة الأولى
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      if (!worksheet) {
        throw new Error('لم يتم العثور على بيانات في ملف الإكسل');
      }
      
      // تحويل ورقة العمل إلى مصفوفة من الكائنات
      const data = XLSX.utils.sheet_to_json(worksheet);
      
      if (data.length === 0) {
        throw new Error('لم يتم العثور على بيانات في ملف الإكسل');
      }
      
      console.log(`📊 تم قراءة ${data.length} سطر من ملف Excel`);
      
      // إنشاء فهرس للباركودات لتسريع البحث
      console.log('🔄 تحميل المنتجات وإنشاء فهرس الباركودات...');
      const allProductsResponse = await apiWithContext.get('/products?limit=50000', 'invoice');
      const allProducts = allProductsResponse.data.products || [];
      
      // إنشاء فهرس سريع للباركودات
      const barcodeIndex = new Map<string, { product: Product, variant: ProductVariant, barcode: ProductBarcode }>();
      
      for (const product of allProducts) {
        for (const variant of product.variants) {
          for (const barcode of variant.barcodes) {
            // فهرسة الباركود الرئيسي
            if (barcode.code && isProductAllowed(product, variant)) {
              barcodeIndex.set(barcode.code, { product, variant, barcode });
            }
            
            // فهرسة الباركودات الإضافية
            if (barcode.additionalCodes && Array.isArray(barcode.additionalCodes)) {
              for (const additionalCode of barcode.additionalCodes) {
                if (additionalCode && isProductAllowed(product, variant)) {
                  barcodeIndex.set(additionalCode, { product, variant, barcode });
                }
              }
            }
          }
        }
      }
      
      console.log(`📦 تم تحميل ${allProducts.length} منتج وفهرسة ${barcodeIndex.size} باركود`);
      
      // تحديد أسماء الأعمدة من الصف الأول
      const firstRow = data[0] as Record<string, any>;
      
      // تحديد أسماء الأعمدة الممكنة بالعربية والإنجليزية
      const possibleBarcodeColumns = ['باركود', 'الباركود', 'barcode', 'code'];
      const possibleProductColumns = ['المنتج', 'اسم المنتج', 'product', 'product name', 'item', 'name'];
      const possibleQuantityColumns = ['الكمية', 'quantity', 'qty'];
      const possiblePriceColumns = ['السعر', 'price'];
      const possibleDiscountColumns = ['الخصم', 'discount'];
      
      // العثور على الأعمدة المستخدمة
      let barcodeColumn = '';
      let productNameColumn = '';
      let quantityColumn = '';
      let priceColumn = '';
      let discountColumn = '';
      
      for (const key of Object.keys(firstRow)) {
        const keyLower = key.toLowerCase();
        if (!barcodeColumn && possibleBarcodeColumns.some(col => keyLower.includes(col.toLowerCase()))) {
          barcodeColumn = key;
        } else if (!productNameColumn && possibleProductColumns.some(col => keyLower.includes(col.toLowerCase()))) {
          productNameColumn = key;
        } else if (!quantityColumn && possibleQuantityColumns.some(col => keyLower.includes(col.toLowerCase()))) {
          quantityColumn = key;
        } else if (!priceColumn && possiblePriceColumns.some(col => keyLower.includes(col.toLowerCase()))) {
          priceColumn = key;
        } else if (!discountColumn && possibleDiscountColumns.some(col => keyLower.includes(col.toLowerCase()))) {
          discountColumn = key;
        }
      }
      
      // التحقق من وجود أعمدة ضرورية على الأقل
      if (!productNameColumn && !barcodeColumn) {
        throw new Error('يجب أن يحتوي ملف الإكسل على عمود "باركود" أو "اسم المنتج" على الأقل');
      }
      
      console.log(`🔍 الأعمدة المكتشفة: باركود="${barcodeColumn}", منتج="${productNameColumn}", كمية="${quantityColumn}"`);
      
      // معالجة البيانات على دفعات لتحسين الأداء
      const BATCH_SIZE = 100; // معالجة 100 منتج في كل دفعة
      const importedProducts: IInvoiceItemExtended[] = [];
      let productsAdded = 0;
      let productsNotFound = 0;
      const notFoundList: Array<{barcode: string, productName: string, rowNumber: number}> = [];
      const totalRows = data.length;
      
      // معالجة البيانات على دفعات
      for (let i = 0; i < totalRows; i += BATCH_SIZE) {
        const batch = data.slice(i, Math.min(i + BATCH_SIZE, totalRows));
        console.log(`⚙️ معالجة الدفعة ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(totalRows / BATCH_SIZE)} (${batch.length} منتج)`);
        
        // معالجة كل صف في الدفعة
        for (let j = 0; j < batch.length; j++) {
          const row = batch[j];
          const rowData = row as Record<string, any>;
          const currentRowNumber = i + j + 2; // +2 لأن Excel يبدأ من 1 وهناك صف العناوين
          
          // استخراج البيانات من الصف
          const barcode = barcodeColumn ? String(rowData[barcodeColumn] || '').trim() : '';
          const productName = productNameColumn ? String(rowData[productNameColumn] || '').trim() : '';
          const quantity = quantityColumn ? parseFloat(String(rowData[quantityColumn] || '1')) : 1;
          const price = priceColumn ? parseFloat(String(rowData[priceColumn] || '0')) : 0;
          const discount = discountColumn ? parseFloat(String(rowData[discountColumn] || '0')) : 0;
          
          // تخطي الصفوف الفارغة
          if (!barcode && !productName) continue;
          
          // إذا كان هناك باركود، نبحث عن المنتج المقابل
          if (barcode) {
            // البحث السريع باستخدام الفهرس
            const barcodeResult = barcodeIndex.get(barcode);
            
            if (barcodeResult) {
              const { product: foundProduct, variant: foundVariant, barcode: foundBarcode } = barcodeResult;
              
              // استخراج معدل الضريبة من تصنيف المتغير أو المنتج
              let taxRate = 0;
              const categoryId = foundVariant.categories && foundVariant.categories.length > 0
                ? foundVariant.categories[0]
                : (foundBarcode?.categories && foundBarcode.categories.length > 0
                  ? foundBarcode.categories[0]
                  : undefined);
                  
              if (categoryId) {
                taxRate = getTaxRateFromCategory(categoryId);
              }
              
              // استخدام نوع السعر المحدد في إعدادات الصفحة أو السعر من الإكسل
              const selectedPrice = price > 0 ? price : getPriceByType(foundBarcode, page?.defaultPriceType || 'retailPrice');
              
              // إضافة المنتج إلى القائمة
              importedProducts.push({
                productId: foundProduct._id,
                productName: foundProduct.name,
                variantId: foundVariant._id,
                barcode: foundBarcode.code,
                barcodeId: foundBarcode._id,
                price: selectedPrice,
                quantity: quantity,
                discount: discount,
                discountType: 'fixed',
                tax: taxRate,
                total: (selectedPrice * quantity) - discount,
                unit: foundVariant.unit || '',
                availableQuantity: foundVariant.warehouseInventory?.find(
                  w => w.warehouseId === formData.warehouseId
                )?.availableQuantity || 0
              });
              
              productsAdded++;
            } else {
              productsNotFound++;
              notFoundList.push({
                barcode: barcode,
                productName: productName || 'غير محدد',
                rowNumber: currentRowNumber
              });
            }
          } else if (productName) {
            // إذا لم يكن هناك باركود ولكن هناك اسم منتج، نضيف المنتج يدويًا
            importedProducts.push({
              productId: '',
              productName: productName,
              barcode: '',
              price: price,
              quantity: quantity,
              discount: discount,
              discountType: 'fixed',
              tax: 0,
              total: (price * quantity) - discount,
              variantId: undefined,
              barcodeId: undefined,
              unit: '',
              availableQuantity: 0
            });
            
            productsAdded++;
          }
        }
        
        // إعطاء المتصفح فرصة للتنفس بين الدفعات
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      
      if (importedProducts.length === 0) {
        throw new Error('لم يتم العثور على منتجات صالحة في ملف الإكسل');
      }
      
      console.log(`✅ تمت معالجة ${importedProducts.length} منتج بنجاح`);
      
      // إضافة المنتجات المستوردة إلى الفاتورة على دفعات
      console.log('💾 إضافة المنتجات إلى الفاتورة...');
      const ADD_BATCH_SIZE = 500; // إضافة 500 منتج في كل مرة
      
      for (let i = 0; i < importedProducts.length; i += ADD_BATCH_SIZE) {
        const batch = importedProducts.slice(i, Math.min(i + ADD_BATCH_SIZE, importedProducts.length));
        
        setFormData(prev => {
          const updatedItems = [...prev.items, ...batch];
          return {
            ...prev,
            items: updatedItems
          };
        });
        
        // إعطاء React فرصة لتحديث الواجهة
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      // إعادة حساب المجاميع مرة واحدة في النهاية
      console.log('🧮 إعادة حساب المجاميع...');
      setFormData(prev => {
        calculateTotals(prev.items);
        return prev;
      });
      
      // حفظ قائمة المنتجات غير الموجودة
      if (notFoundList.length > 0) {
        setNotFoundProducts(notFoundList);
      }
      
      // عرض رسالة نجاح
      const successMessage = `✅ تم استيراد ${productsAdded} منتج بنجاح${productsNotFound > 0 ? ` (⚠️ لم يتم العثور على ${productsNotFound} منتج)` : ''}`;
      setBarcodeMessage(successMessage);
      setShowBarcodeMessage(true);
      
      // فتح نافذة المنتجات غير الموجودة إذا كان هناك منتجات
      if (notFoundList.length > 0) {
        setTimeout(() => {
          setShowNotFoundDialog(true);
        }, 1000);
      }
      
      // مسح مرجع ملف الإكسل
      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = '';
      }
      
      console.log('🎉 اكتمل الاستيراد بنجاح!');
      
    } catch (error: any) {
      console.error('❌ خطأ في استيراد ملف الإكسل:', error);
      setBarcodeMessage(`خطأ في استيراد ملف الإكسل: ${error.message}`);
      setShowBarcodeMessage(true);
    } finally {
      setImportLoading(false);
    }
  };
  
  // إنشاء وتنزيل قالب إكسل للمنتجات
  const handleDownloadExcelTemplate = () => {
    try {
      // إنشاء مصنف جديد
      const workbook = XLSX.utils.book_new();
      
      // إنشاء بيانات للقالب مع أعمدة نموذجية وصفوف فارغة للتعبئة
      const templateData = [
        // الصف الأول يحتوي على أسماء الأعمدة
        {
          'الباركود': '',
          'اسم المنتج': '',
          'الكمية': 1,
          'السعر': 0,
          'الخصم': 0
        },
        // إضافة صف نموذجي للتوضيح
        {
          'الباركود': '123456789',
          'اسم المنتج': 'منتج نموذجي',
          'الكمية': 5,
          'السعر': 100,
          'الخصم': 10
        }
      ];
      
      // تحويل البيانات إلى ورقة عمل
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      
      // إضافة أنماط للأعمدة لتحسين العرض
      const columnWidths = [
        { wch: 20 }, // الباركود
        { wch: 30 }, // اسم المنتج
        { wch: 10 }, // الكمية
        { wch: 15 }, // السعر
        { wch: 15 }  // الخصم
      ];
      
      worksheet['!cols'] = columnWidths;
      
      // إضافة ورقة العمل إلى المصنف
      XLSX.utils.book_append_sheet(workbook, worksheet, 'قالب المنتجات');
      
      // تنزيل الملف
      XLSX.writeFile(workbook, 'قالب_منتجات_الفاتورة.xlsx');
      
      // عرض رسالة نجاح
      setBarcodeMessage('تم تنزيل قالب الإكسل بنجاح');
      setShowBarcodeMessage(true);
    } catch (error: any) {
      console.error('Error creating Excel template:', error);
      setBarcodeMessage(`حدث خطأ أثناء إنشاء قالب الإكسل: ${error.message}`);
      setShowBarcodeMessage(true);
    }
  };
  
  // العودة إلى صفحة قائمة الفواتير
  const handleBackToList = () => {
    navigate(`/invoices/${pageId}`);
  };
  
  // إلغاء دون حفظ
  const handleCancel = () => {
    navigate(`/invoices/${pageId}`);
  };

  // إعادة تعيين النموذج لفاتورة جديدة
  const resetFormForNewInvoice = () => {
    // إغلاق نافذة النجاح أولاً
    setShowSuccessDialog(false);
    
    // إعادة تحميل الصفحة بالكامل للتأكد من إعادة تعيين جميع البيانات
    window.location.href = `/invoices/${pageId}/new`;
  };

  // إغلاق نافذة النجاح
  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
  };

  // فتح نافذة البحث عن خدمات المنتج
  const handleOpenServiceProductSearch = () => {
    setIsServiceProductSearchOpen(true);
  };

  // إغلاق نافذة البحث عن خدمات المنتج
  const handleCloseServiceProductSearch = () => {
    setIsServiceProductSearchOpen(false);
  };

  // تحميل طرق الدفع المتاحة
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const methods = await paymentMethodService.getActivePaymentMethodsForCurrentUser();
        setAvailablePaymentMethods(methods as paymentMethodService.PaymentMethod[]);
      } catch (err) {
        console.error('خطأ في جلب طرق الدفع:', err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // حساب رسوم طريقة الدفع
  const calculatePaymentFees = async (paymentMethodId: string, amount: number): Promise<{
    fixedFee: number;
    variableFee: number;
    totalFees: number;
  }> => {
    try {
      const response = await axiosInstance.post('/finance/payment-methods/calculate-fees', {
        paymentMethodId,
        amount
      });
      const data = response.data;
      return {
        fixedFee: Number(data.fixedFee || 0),
        variableFee: Number(data.variableFee || 0),
        totalFees: Number(data.totalFees || 0)
      };
    } catch (err) {
      console.error('خطأ في حساب رسوم طريقة الدفع:', err);
      return { fixedFee: 0, variableFee: 0, totalFees: 0 };
    }
  };

  // التعامل مع اختيار خدمة منتج
  const handleSelectServiceProduct = async (serviceProduct: any, paymentMethodId?: string) => {
    try {
      // استخدام الضريبة الخاصة بالمنتج الخدمي فقط (إذا كانت الضريبة مفعلة في الفاتورة)
      let taxRate = 0;
      
      // التحقق من تفعيل الضريبة في الفاتورة أولاً
      if (page?.enableTax) {
        // استخدام ضريبة المنتج الخدمي إذا كانت موجودة
        if (serviceProduct.taxRate && serviceProduct.taxRate > 0) {
          taxRate = serviceProduct.taxRate;
          console.log(`استخدام ضريبة المنتج الخدمي: ${taxRate}% للمنتج ${serviceProduct.name}`);
        } else {
          // إذا لم توجد ضريبة خاصة بالمنتج الخدمي، لا نطبق أي ضريبة
          taxRate = 0;
          console.log(`لا توجد ضريبة مسجلة للمنتج الخدمي: ${serviceProduct.name}`);
        }
      } else {
        console.log(`الضريبة غير مفعلة في الفاتورة`);
      }

      let servicePrice = serviceProduct.price;
      let serviceTotal = serviceProduct.price;
      let paymentFeesTotal = 0;

      // إذا تم اختيار طريقة دفع، سعر الخدمة يصبح مجموع الرسوم المحسوب على إجمالي الفاتورة
      if (paymentMethodId) {
        const paymentMethod = availablePaymentMethods.find(m => m.id === paymentMethodId);
        if (paymentMethod) {
          // حساب الرسوم بشكل تكراري على الإجمالي المتوقع النهائي (القيمة الحالية + الرسوم)
          // هذا يضمن أن رسوم المنتج الخدمي تطابق رسوم طريقة الدفع المحسوبة على الإجمالي النهائي
          let projectedTotal = formData.total || 0;
          let calculatedFees = 0;
          
          // التكرار لحساب الرسوم على الإجمالي المتوقع (يحتوي على الرسوم نفسها)
          for (let i = 0; i < 10; i++) {
            const fees = await calculatePaymentFees(paymentMethodId, projectedTotal);
            const newFees = fees.totalFees;
            
            // إذا لم يتغير الرسم significantly، توقف
            if (Math.abs(newFees - calculatedFees) < 0.01) break;
            
            calculatedFees = newFees;
            projectedTotal = (formData.total || 0) + calculatedFees;
          }
          
          // تقريب الرسوم إلى منزلتين عشريتين
          const roundedFees = Math.round(calculatedFees * 100) / 100;
          paymentFeesTotal = roundedFees;
          servicePrice = roundedFees; // السعر يصبح مجموع الرسوم
          serviceTotal = roundedFees;
          console.log(`رسوم طريقة الدفع ${paymentMethod.name} على إجمالي الفاتورة المتوقع ${projectedTotal.toFixed(2)}: ${calculatedFees.toFixed(2)} ر.س`);
          console.log(`سعر الخدمة أصبح مجموع الرسوم: ${serviceTotal.toFixed(2)} ر.س`);
        }
      }

      // إنشاء عنصر خدمة منتج جديد
      const newServiceItem: IInvoiceItemExtended = {
        productId: serviceProduct._id,
        productName: serviceProduct.name,
        barcode: serviceProduct.barcode,
        price: servicePrice,
        quantity: 1,
        discount: 0,
        discountType: 'fixed',
        tax: taxRate, // استخدام ضريبة المنتج الخدمي فقط
        total: serviceTotal,
        unit: 'خدمة',
        availableQuantity: 999, // خدمات المنتج لا تحتاج لمخزون
        isServiceProduct: true, // علامة للتمييز
        paymentMethodId: paymentMethodId, // حفظ طريقة الدفع المختارة
        paymentFees: paymentFeesTotal // حفظ قيمة الرسوم
      };

      // إضافة العنصر للفاتورة
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, newServiceItem]
      }));

      // إعادة حساب المجاميع
      calculateTotals([...formData.items, newServiceItem]);

      // إظهار رسالة نجاح
      const successMessage = paymentMethodId
        ? `تم إضافة خدمة ${serviceProduct.name} مع رسوم الدفع بنجاح`
        : `تم إضافة خدمة ${serviceProduct.name} بنجاح`;
      setBarcodeMessage(successMessage);
      setShowBarcodeMessage(true);

      // إغلاق نافذة البحث
      setIsServiceProductSearchOpen(false);
    } catch (error) {
      console.error('Error adding service product:', error);
      setBarcodeMessage('حدث خطأ أثناء إضافة خدمة المنتج');
      setShowBarcodeMessage(true);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }
  
  // Render if page not found
  if (!page) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        لم يتم العثور على صفحة الفاتورة المطلوبة
      </Alert>
    );
  }
  
  // If invalid ID, show a special error message with a way to go back
  if (invalidId) {
    return (
      <Box sx={{ maxWidth: '100%', bgcolor: '#f8f9fa', p: { xs: 3, sm: 4 } }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            خطأ في تحميل الفاتورة
          </Typography>
          
          <Alert severity="error" sx={{ width: '100%', fontSize: '1rem' }}>
            {error || 'معرف الفاتورة غير صالح. الرجاء التحقق من الرابط المستخدم للوصول إلى هذه الصفحة.'}
          </Alert>
          
          <Typography variant="body1" align="center">
            يبدو أن هناك مشكلة في معرف الفاتورة في الرابط. قد يكون الرابط غير صحيح أو تمت إزالة الفاتورة.
          </Typography>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => pageId ? navigate(`/invoices/${pageId}`) : navigate('/invoices')}
            >
              العودة إلى قائمة الفواتير
            </Button>
            
            {pageId && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/invoices/${pageId}/new`)}
              >
                إنشاء فاتورة جديدة
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
// إذا كان نوع الفاتورة هو مرتجع فاتورة استيراد، نستخدم المكون المخصص له
if (page && page.type === 'import_invoice_return') {
  return (
    <ImportInvoiceReturnForm
      pageId={pageId || ''}
      invoiceId={invoiceId}
      page={page}
      onSave={async (data) => {
        console.log('🔄 بدء عملية الحفظ في InvoiceFormPage');
        setSaving(true);
        try {
          let invoiceResponse;
          if (isEditing && invoiceId) {
            console.log('📝 تحديث فاتورة موجودة:', invoiceId);
            invoiceResponse = await invoiceService.updateInvoice(invoiceId, data);
            setSavedInvoiceId(invoiceId);
          } else {
            console.log('➕ إنشاء فاتورة جديدة');
            invoiceResponse = await invoiceService.createInvoice(data);
            setSavedInvoiceId(invoiceResponse._id);
          }
          console.log('✅ تم حفظ الفاتورة بنجاح');
          
          // خصم نقاط الولاء إذا تم استخدامها
          if ((data as any).loyaltyPointsRedeemed && (data as any).loyaltyPointsRedeemed > 0 && data.customerId) {
            try {
              console.log(`🎁 خصم ${(data as any).loyaltyPointsRedeemed} نقطة ولاء للعميل`);
              await axiosInstance.post('/customers/redeem-loyalty-points', {
                customerId: data.customerId,
                points: (data as any).loyaltyPointsRedeemed,
                discountAmount: (data as any).loyaltyDiscountAmount || 0,
                invoiceNumber: invoiceResponse.invoiceNumber
              });
              console.log('✅ تم خصم نقاط الولاء بنجاح');
            } catch (loyaltyErr: any) {
              console.error('⚠️ خطأ في خصم نقاط الولاء:', loyaltyErr);
              // لا نوقف العملية - الفاتورة محفوظة بنجاح
              alert('تم حفظ الفاتورة بنجاح ولكن حدث خطأ في خصم نقاط الولاء. يرجى التحقق من رصيد العميل.');
            }
          }
          
          setShowPrintButton(true);
        } catch (err: any) {
          console.error('❌ خطأ في حفظ الفاتورة في InvoiceFormPage:', err);
          console.error('Error response:', err.response);
          console.error('Error response data:', err.response?.data);
          
          // استخراج رسالة الخطأ
          let errorMessage = 'حدث خطأ أثناء حفظ الفاتورة. يرجى المحاولة مرة أخرى.';
          if (err.response && err.response.data && err.response.data.message) {
            errorMessage = err.response.data.message;
            console.log('📝 استخدام رسالة الخطأ من الخادم:', errorMessage);
          } else if (err.message) {
            errorMessage = err.message;
            console.log('📝 استخدام رسالة الخطأ العامة:', errorMessage);
          }
          
          console.log('🚨 تعيين رسالة الخطأ:', errorMessage);
          setError(errorMessage);
          setSaving(false);
          
          // رمي الخطأ مرة أخرى ليتم التعامل معه في ImportInvoiceReturnForm
          throw err;
        }
      }}
      onCancel={handleCancel}
      onPrint={handlePrintInvoice}
      showPrintButton={showPrintButton}
      savedInvoiceId={savedInvoiceId}
    />
  );
}

// للأنواع الأخرى من الفواتير، نستخدم النموذج العادي
return (
    <Box sx={{ maxWidth: '100%', bgcolor: '#f8f9fa', p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          backgroundImage: 'linear-gradient(to right, #1976d2, #2196f3)',
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon fontSize="large" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
              {isEditing ? 'تعديل الفاتورة' : 'فاتورة جديدة'} - {page.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              sx={{ 
                color: '#1976d2', 
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              إلغاء
            </Button>
            
            {showPrintButton ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintInvoice}
                  sx={{
                    ml: 1,
                    bgcolor: '#2196f3',
                    '&:hover': { bgcolor: '#1976d2' }
                  }}
                >
                  طباعة
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToList}
                  sx={{
                    ml: 1,
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#388e3c' }
                  }}
                >
                  العودة للقائمة
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' }
                }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'حفظ الفاتورة'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Barcode Scanner Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          borderLeft: '4px solid #2196f3'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <QrCodeIcon fontSize="large" color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1976d2', mb: 2 }}>
              مسح الباركود
            </Typography>
          </Box>
          
          {/* إضافة زر استيراد الإكسل إذا كانت الخاصية مفعلة في إعدادات الصفحة */}
          {page?.enableExcelImport && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <input
                type="file"
                ref={excelFileInputRef}
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                onChange={handleExcelImport}
              />
              <Tooltip title="تنزيل قالب إكسل فارغ للمنتجات" arrow>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleDownloadExcelTemplate}
                  sx={{
                    mb: 2,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: '#f3e5f5'
                    }
                  }}
                >
                  تنزيل قالب
                </Button>
              </Tooltip>
              <Tooltip title="استيراد المنتجات من ملف إكسل" arrow>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileUploadIcon />}
                  onClick={() => excelFileInputRef.current?.click()}
                  disabled={importLoading}
                  sx={{
                    mb: 2,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: '#e3f2fd'
                    }
                  }}
                >
                  {importLoading ? <CircularProgress size={24} /> : 'استيراد منتجات من إكسل'}
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
        <TextField
          fullWidth
          label="مسح أو إدخال الباركود"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyDown={handleBarcodeKeyDown}
          inputRef={barcodeInputRef}
          autoFocus
          placeholder="امسح الباركود أو أدخله يدويًا ثم اضغط Enter"
          sx={{ 
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#2196f3',
                borderWidth: 2
              }
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={handleBarcodeScan} 
                  color="primary"
                  sx={{ 
                    bgcolor: '#e3f2fd',
                    '&:hover': { bgcolor: '#bbdefb' }
                  }}
                >
                  <QrCodeIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          امسح الباركود بالماسح الضوئي أو أدخله يدويًا ثم اضغط Enter لإضافة المنتج مباشرة إلى الفاتورة
        </Typography>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Invoice Details */}
        <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 1 } }}>
          <Card 
            elevation={3} 
            sx={{ 
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ pb: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2,
                color: '#1976d2'
              }}>
                <InventoryIcon />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  تفاصيل الفاتورة
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={12}>
                  <TextField
                    fullWidth
                    label="رقم الفاتورة"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    required
                    size="small"
                    disabled={page?.lockInvoiceNumber || false}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={12}>
                  <TextField
                    fullWidth
                    label="التاريخ والوقت"
                    name="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    size="small"
                    disabled={page?.lockDate || false}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={12}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>الحالة</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      label="الحالة"
                      disabled={page?.lockStatus || false}
                    >
                      <MenuItem value="draft">مسودة</MenuItem>
                      <MenuItem value="completed">مكتملة</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={12}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>المستودع</InputLabel>
                    <Select
                      name="warehouseId"
                      value={formData.warehouseId || ''}
                      onChange={handleWarehouseChange}
                      label="المستودع"
                      disabled={page?.lockWarehouse || false}
                    >
                      {warehouses.map(warehouse => (
                        <MenuItem key={warehouse._id} value={warehouse._id}>
                          {warehouse.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Customer/Supplier Selection based on invoice type */}
                <Grid item xs={12}>
                  {page && ['sales_invoice', 'sales_return'].includes(page.type) && (
                    <>
                      {/* القائمة المنسدلة للعملاء مع البحث الديناميكي */}
                      <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <Typography variant="body1">{option.name}</Typography>
                              {option.phone && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                  {option.phone}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        value={customers.find(c => c._id === formData.customerId) || null}
                        onChange={(_, value) => handleCustomerChange(value)}
                        onInputChange={(_, newInputValue) => {
                          // البحث الديناميكي عند الكتابة
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchCustomersFromServer(newInputValue);
                          }, 500); // انتظار 500ms بعد التوقف عن الكتابة
                          setSearchTimeout(timeout);
                        }}
                        loading={isLoadingCustomers}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="العميل (ابحث بالاسم)"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ mb: 2 }}
                        noOptionsText="لا توجد نتائج - ابدأ بالكتابة للبحث"
                        loadingText="جاري البحث..."
                      />
                      
                      {/* واجهة إضافة عميل جديد - تظهر دائماً إذا كانت الخاصية مفعلة */}
                      {page.enableNewCustomer && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                            إضافة بيانات العميل الجديد
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ position: 'relative' }}>
                                <TextField
                                  label="رقم الهاتف (للبحث)"
                                  fullWidth
                                  size="small"
                                  value={customerPhone}
                                  onChange={handlePhoneChange}
                                  disabled={isCustomerFieldsLocked}
                                  sx={{
                                    mb: 0.5,
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: isCustomerFieldsLocked ? '#f5f5f5' : '#f0f8ff',
                                      borderColor: matchingCustomers.length > 0 ? '#1976d2' : 'inherit',
                                      boxShadow: matchingCustomers.length > 0 ? '0 0 0 1px #1976d2' : 'none'
                                    }
                                  }}
                                  placeholder="أدخل رقم الهاتف للبحث عن العملاء"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon color="primary" fontSize="small" />
                                      </InputAdornment>
                                    ),
                                    endAdornment: matchingCustomers.length > 0 ? (
                                      <InputAdornment position="end">
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          size="small"
                                          onClick={() => setIsCustomerSearchOpen(true)}
                                          sx={{
                                            minWidth: 'auto',
                                            py: 0.5,
                                            px: 1.5,
                                            borderRadius: '50px',
                                            boxShadow: 2,
                                            animation: 'pulse 1.5s infinite'
                                          }}
                                        >
                                          {`${matchingCustomers.length} عميل`}
                                        </Button>
                                      </InputAdornment>
                                    ) : null
                                  }}
                                />
                                {matchingCustomers.length > 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: 0,
                                      right: 0,
                                      backgroundColor: '#e3f2fd',
                                      borderRadius: '0 0 4px 4px',
                                      padding: '4px 8px',
                                      zIndex: 1,
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      border: '1px solid #bbdefb',
                                      borderTop: 0,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="primary"
                                      fontWeight="bold"
                                      onClick={() => setIsCustomerSearchOpen(true)}
                                      sx={{
                                        cursor: 'pointer',
                                        display: 'block',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      تم العثور على {matchingCustomers.length} عميل مطابق - انقر لعرض النتائج
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color={matchingCustomers.length > 0 ? "success.main" : "primary"}
                                sx={{ display: 'block', mb: 1, fontWeight: matchingCustomers.length > 0 ? 'bold' : 'normal' }}
                              >
                                {matchingCustomers.length > 0
                                  ? `تم العثور على ${matchingCustomers.length} عميل، يمكنك النقر لعرض النتائج`
                                  : `أدخل رقم الهاتف للبحث عن العملاء الحاليين`
                                }
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="اسم العميل"
                                fullWidth
                                size="small"
                                value={customerName}
                                onChange={handleCustomerNameChange}
                                disabled={isCustomerFieldsLocked}
                                sx={{
                                  mb: 1,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: isCustomerFieldsLocked ? '#f5f5f5' : 'inherit'
                                  }
                                }}
                                placeholder="أدخل اسم العميل"
                              />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={() => {
                                // حفظ العميل الجديد مباشرة من الحقول
                                if (customerPhone && customerName) {
                                  // التحقق من عدد أرقام الهاتف بناءً على إعدادات الصفحة
                                  const phoneDigitsLimit = page?.customerPhoneDigitsLimit || 10;
                                  const cleanedPhone = customerPhone.replace(/[\s-+()]/g, '');
                                  const phoneDigits = cleanedPhone.replace(/\D/g, '');
                                  
                                  if (phoneDigits.length !== phoneDigitsLimit) {
                                    setBarcodeMessage(`رقم الهاتف يجب أن يحتوي على ${phoneDigitsLimit} رقم بالضبط. العدد الحالي: ${phoneDigits.length}`);
                                    setShowBarcodeMessage(true);
                                    return;
                                  }
                                  
                                  handleSaveNewCustomer({
                                    name: customerName,
                                    phone: customerPhone,
                                    phoneDigitsLimit: phoneDigitsLimit,
                                    type: 'customer',
                                    status: 'active',
                                    category: 'regular'
                                  });
                                } else {
                                  // عرض رسالة خطأ إذا كان أحد الحقول فارغاً
                                  setBarcodeMessage('يرجى إدخال رقم الهاتف واسم العميل');
                                  setShowBarcodeMessage(true);
                                }
                              }}
                              disabled={!customerPhone || !customerName}
                            >
                              حفظ العميل
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={handleOpenCustomerDetails}
                              sx={{ mr: 1 }}
                            >
                              إضافة معلومات تفصيلية
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                  
                  {page && ['purchase_invoice', 'purchase_return'].includes(page.type) && (
                    <Autocomplete
                      options={suppliers}
                      getOptionLabel={(option) => option.name}
                      value={suppliers.find(s => s._id === formData.supplierId) || null}
                      onChange={(_, value) => handleSupplierChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="المورد"
                          required
                          size="small"
                        />
                      )}
                      sx={{ mb: 2 }}
                    />
                  )}
                
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ملاحظات"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
            
            {/* Totals Summary */}
            <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="flex-end">
              <Divider />
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: '0 0 8px 8px' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShoppingCartIcon />
                    ملخص المبالغ
                  </Box>
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {page.enableTax && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1" color="text.secondary">المجموع قبل الضريبة:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" fontWeight="bold">
                          {(formData.subtotalBeforeTax || 0).toFixed(2)} ريال
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body1" color="text.secondary">قيمة الضريبة:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" fontWeight="bold">
                          {(formData.tax || 0).toFixed(2)} ريال
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body1" color="text.secondary">المجموع بعد الضريبة:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" fontWeight="bold">
                          {formData.subtotal.toFixed(2)} ريال
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  {!page.enableTax && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1" color="text.secondary">المجموع الفرعي:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" fontWeight="bold">
                          {formData.subtotal.toFixed(2)} ريال
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary">الخصم:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        variant="outlined"
                        name="discount"
                        type="number"
                        size="small"
                        value={discountInput}
                        disabled={page?.lockDiscount || false}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDiscountInput(value);
                          
                          // تحديث القيمة الرقمية للحسابات
                          const numericValue = value === '' ? 0 : (parseFloat(value) || 0);
                          setFormData(prev => ({
                            ...prev,
                            discount: numericValue
                          }));
                          setOriginalDiscountValue(numericValue);
                          calculateTotals(formData.items, numericValue, formData.discountType);
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                          endAdornment:
                            <InputAdornment position="end">
                              {formData.discountType === 'percentage' ? '%' : 'ريال'}
                            </InputAdornment>
                        }}
                        sx={{ width: '100%' }}
                      />
                      <FormControl component="fieldset">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Button
                            variant={formData.discountType === 'fixed' ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            disabled={page?.lockDiscount || false}
                            onClick={() => {
                              const newDiscountType = 'fixed';
                              setFormData(prev => ({
                                ...prev,
                                discountType: newDiscountType
                              }));
                              // إعادة حساب المجاميع فوراً مع النوع الجديد
                              calculateTotals(formData.items, formData.discount, newDiscountType);
                            }}
                            sx={{ minWidth: '80px', py: 0.5 }}
                          >
                            ثابت
                          </Button>
                          <Button
                            variant={formData.discountType === 'percentage' ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            disabled={page?.lockDiscount || false}
                            onClick={() => {
                              const newDiscountType = 'percentage';
                              setFormData(prev => ({
                                ...prev,
                                discountType: newDiscountType
                              }));
                              // إعادة حساب المجاميع فوراً مع النوع الجديد
                              calculateTotals(formData.items, formData.discount, newDiscountType);
                            }}
                            sx={{ minWidth: '80px', py: 0.5 }}
                          >
                            نسبة %
                          </Button>
                        </Box>
                      </FormControl>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  {/* إضافة عرض خصم الكوبون إذا كان مفعلاً */}
                  {formData.marketingEnabled && formData.couponDiscount && formData.couponDiscount > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1" color="secondary">
                          خصم الكوبون:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" color="secondary">
                          {formData.couponDiscountType === 'percentage'
                            ? `${formData.couponDiscount}%`
                            : `${formData.couponDiscount.toFixed(2)} ريال`}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" color="secondary">
                          قيمة خصم الكوبون:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" color="secondary">
                          {/* استخدام قيمة الخصم المطبقة فعلياً بدلاً من إعادة حسابها */}
                          {(formData.appliedCouponDiscount || 0).toFixed(2)} ريال
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={6}>
                    <Typography variant="h6" color="primary" fontWeight="bold">الإجمالي النهائي:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" align="right" color="primary" fontWeight="bold">
                      {formData.total.toFixed(2)} ريال
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* إضافة معالج تغيير طرق الدفع */}
                {page && page.enablePaymentMethods && (
                  <Box mt={3}>
                    <PaymentMethodsSection
                      invoiceTotal={formData.total}
                      initialPaymentMethods={formData.paymentMethods || []}
                      onPaymentMethodsChange={handlePaymentMethodsChange}
                      disabled={saving}
                      cashAccountId={page.cashAccountId}
                      invoiceItems={formData.items || []}
                    />
                  </Box>
                )}
                
                {/* إضافة قسم الدفع الجزئي */}
                {page && page.enablePartialPayment && (
                  <Box mt={3}>
                    <PartialPaymentInput
                      totalAmount={formData.total}
                      paidAmount={paidAmount}
                      onPaidAmountChange={setPaidAmount}
                      disabled={saving}
                    />
                  </Box>
                )}
                
                {/* إضافة قسم التسويق */}
                {page && page.enableMarketing && (
                  <Box mt={3}>
                    <MarketingSection
                      initialEnabled={formData.marketingEnabled || false}
                      initialCouponCodes={formData.couponCodes || []}
                      availableCoupons={formData.availableCoupons || []}
                      disabled={saving}
                      invoiceItems={formData.items || []}
                      pageId={pageId}
                      onChange={handleMarketingChange}
                    />
                  </Box>
                )}

                {/* إضافة قسم نقاط الولاء */}
                {page && page.enableLoyaltyPoints && (
                  <Box mt={3}>
                    <InvoiceLoyaltyPoints
                      customerId={formData.customerId}
                      customerName={formData.customerName}
                      invoiceTotal={formData.total || 0}
                      enabled={true}
                      pageId={page?._id}
                      onPointsRedeem={handleLoyaltyPointsRedeem}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        
        {/* Invoice Items */}
        <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 2 } }}>
          <Card 
            elevation={3} 
            sx={{ 
              borderRadius: 2,
              height: '100%'
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: '#1976d2'
                }}>
                  <CartIcon />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    المنتجات ({formData.items.length})
                  </Typography>
                  <Box sx={{
                    bgcolor: '#e3f2fd',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    border: '1px solid #bbdefb'
                  }}>
                    <Typography variant="body2" color="primary.main" fontWeight="bold">
                      إجمالي الكميات: {formData.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0)} قطعة
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {page?.enableExcelImport && formData.items.length > 0 && (
                    <Tooltip title="عرض بيانات المنتجات في جدول">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          // يمكن إضافة وظيفة لعرض المنتجات في شكل جدول هنا
                          console.log('عرض بيانات المنتجات في جدول');
                        }}
                      >
                        <TableRowsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{
                    borderRadius: 20,
                    boxShadow: 2,
                    mr: 1
                  }}
                >
                  إضافة منتج
                </Button>
                  {page?.enableServiceProducts && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<AddIcon />}
                      onClick={handleOpenServiceProductSearch}
                      sx={{
                        borderRadius: 20,
                        boxShadow: 1
                      }}
                    >
                      إضافة خدمة
                    </Button>
                  )}
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
              
              {/* إضافة Pagination للجدول */}
              {formData.items.length > 50 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <TablePagination
                    component="div"
                    count={formData.items.length}
                    page={tablePage}
                    onPageChange={(_, newPage) => setTablePage(newPage)}
                    rowsPerPage={tableRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setTableRowsPerPage(parseInt(e.target.value, 10));
                      setTablePage(0);
                    }}
                    rowsPerPageOptions={[50, 100, 200, 500]}
                    labelRowsPerPage="عدد الصفوف:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                  />
                </Box>
              )}
              
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell width="60%">المنتج</TableCell>
                      <TableCell width="18%">متغير المنتج</TableCell>
                      {page.enableTax ? (
                        <>
                          <TableCell>السعر شامل الضريبة</TableCell>
                          <TableCell>السعر قبل الضريبة</TableCell>
                          <TableCell>الضريبة %</TableCell>
                          <TableCell>قيمة الضريبة</TableCell>
                        </>
                      ) : (
                        <TableCell>السعر</TableCell>
                      )}
                      <TableCell>الكمية</TableCell>
                      <TableCell>الخصم</TableCell>
                      <TableCell>المجموع</TableCell>
                      <TableCell>حذف</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={page.enableTax ? 10 : 7} align="center" sx={{ py: 4 }}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            color: 'text.secondary'
                          }}>
                            <ShoppingCartIcon sx={{ fontSize: 48, mb: 1, color: '#bdbdbd' }} />
                            <Typography variant="body1">
                              لا توجد منتجات في الفاتورة
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              يمكنك إضافة منتج عن طريق مسح الباركود أو النقر على "إضافة منتج"
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      // عرض الصفوف حسب الصفحة الحالية فقط
                      formData.items
                        .slice(tablePage * tableRowsPerPage, tablePage * tableRowsPerPage + tableRowsPerPage)
                        .map((item, displayIndex) => {
                          // حساب الفهرس الحقيقي في المصفوفة الأصلية
                          const index = tablePage * tableRowsPerPage + displayIndex;
                          return (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': { bgcolor: '#f5f5f5' },
                            transition: 'background-color 0.2s',
                            ...(item.isServiceProduct && {
                              bgcolor: '#e3f2fd', // خلفية زرقاء فاتحة لخدمات المنتج
                              '&:hover': { bgcolor: '#bbdefb' }
                            })
                          }}
                        >
                          {/* Product Name Cell */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.isServiceProduct && (
                                  <Chip
                                    label="خدمة"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                )}
                                {/* عرض اسم المنتج كنص عادي بدون إطار */}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'medium',
                                    color: '#000000',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.3
                                  }}
                                >
                                  {item.productName || 'غير محدد'}
                                </Typography>
                              </Box>
                              
                              {/* الاحتفاظ بزر البحث عن متغيرات المنتج */}
                              <Tooltip title="بحث عن متغيرات المنتج">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openAttributeSearch(index)}
                                  sx={{
                                    bgcolor: '#e3f2fd',
                                    '&:hover': { bgcolor: '#bbdefb' }
                                  }}
                                >
                                  <SearchIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          
                          {/* Product Variant Cell */}
                          <TableCell>
                            {/* عرض متغيرات المنتج بدون إمكانية التعديل */}
                            {item.productId && (
                              <>
                                {/* عرض متغيرات المنتج المحددة فقط */}
                                {item.variantId && selectedProductVariants[index] && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      متغيرات المنتج:
                                    </Typography>
                                    {Object.entries(selectedProductVariants[index]?.attributes || {}).map(([key, value], idx) => (
                                      <Chip
                                        key={idx}
                                        label={`${key}: ${value}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ m: 0.5, fontWeight: 'medium' }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                
                                {/* عرض الباركود المحدد فقط بدون إمكانية التعديل */}
                                {item.barcode && (
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    label="الباركود"
                                    value={item.barcode}
                                    fullWidth
                                    disabled
                                    sx={{
                                      mt: 1,
                                      '& .MuiInputBase-input.Mui-disabled': {
                                        WebkitTextFillColor: '#000000',
                                        color: '#000000'
                                      }
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </TableCell>
                          
                          {/* Price Cells */}
                          {page.enableTax ? (
                            <>
                              {/* سعر شامل الضريبة */}
                              <TableCell>
                                <TextField
                                  variant="outlined"
                                  type="number"
                                  size="small"
                                  value={item.price}
                                  disabled={page?.lockPrice || false}
                                  onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                  sx={{ width: 100 }}
                                />
                                {item.barcode && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    باركود: {item.barcode}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* سعر قبل الضريبة */}
                              <TableCell>
                                <Typography variant="body2" key={`${formData.discount ?? 0}-${formData.discountType ?? 'fixed'}-${isLoadedForEdit}`}>
                                  {(() => {
                                    // إذا كانت الفاتورة محملة للتعديل، لا نطبق خصم إضافي
                                    if (isLoadedForEdit) {
                                      // حساب السعر قبل الضريبة مباشرة من القيم المحملة
                                      const priceWithDiscount = item.total / item.quantity; // سعر الوحدة بعد الخصم المحفوظ
                                      const priceBeforeTax = priceWithDiscount / (1 + (item.tax / 100));
                                      return priceBeforeTax.toFixed(2);
                                    }
                                    
                                    // للفواتير الجديدة، نحسب معامل الخصم الإجمالي للفاتورة
                                    const invoiceDiscount = Number(formData.discount) || 0;
                                    const invoiceDiscountType = formData.discountType || 'fixed';
                                    
                                    // حساب معامل الخصم الفعلي (فقط على المنتجات غير الخدمية)
                                    let discountFactor = 1;
                                    if (invoiceDiscount > 0) {
                                      if (invoiceDiscountType === 'percentage') {
                                        // إذا كان الخصم نسبة مئوية
                                        discountFactor = 1 - (invoiceDiscount / 100);
                                      } else {
                                        // إذا كان الخصم مبلغاً ثابتاً
                                        const subtotal = formData.items.reduce((total, i) => total + (i.total || 0), 0);
                                        const subtotalNonService = formData.items.reduce((total, i) => {
                                          return i.isServiceProduct ? total : total + (i.total || 0);
                                        }, 0);
                                        const baseSubtotal = subtotalNonService > 0 ? subtotalNonService : subtotal;
                                        if (baseSubtotal > 0) {
                                          discountFactor = Math.max(0, 1 - (invoiceDiscount / baseSubtotal));
                                        }
                                      }
                                    }
                                    
                                    // حساب السعر قبل الضريبة بعد تطبيق الخصم
                                    // المنتجات الخدمية لا تتأثر بالخصم الكلي
                                    const priceWithDiscount = item.total / item.quantity; // سعر الوحدة بعد الخصم
                                    const priceBeforeTax = priceWithDiscount / (1 + (item.tax / 100));
                                    const discountedPriceBeforeTax = item.isServiceProduct ? priceBeforeTax : priceBeforeTax * discountFactor;
                                    
                                    return discountedPriceBeforeTax.toFixed(2);
                                  })()}
                                </Typography>
                              </TableCell>
                              
                              {/* نسبة الضريبة */}
                              <TableCell>
                                <TextField
                                  variant="outlined"
                                  type="number"
                                  size="small"
                                  value={item.tax}
                                  disabled={page?.lockProductTax || false}
                                  onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                                  InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              
                              {/* قيمة الضريبة */}
                              <TableCell>
                                <Typography variant="body2" key={`${formData.discount ?? 0}-${formData.discountType ?? 'fixed'}-${isLoadedForEdit}`}>
                                  {(() => {
                                    // إذا كانت الفاتورة محملة للتعديل، لا نطبق خصم إضافي
                                    if (isLoadedForEdit) {
                                      // حساب قيمة الضريبة مباشرة من القيم المحملة
                                      const priceWithDiscount = item.total / item.quantity; // سعر الوحدة بعد الخصم المحفوظ
                                      const taxRate = item.tax / 100;
                                      const priceBeforeTax = priceWithDiscount / (1 + taxRate);
                                      const taxAmount = priceWithDiscount - priceBeforeTax;
                                      const totalTaxAmount = taxAmount * item.quantity; // ضرب في الكمية للحصول على إجمالي الضريبة
                                      return totalTaxAmount.toFixed(2);
                                    }
                                    
                                    // للفواتير الجديدة، نحسب معامل الخصم الإجمالي للفاتورة
                                    const invoiceDiscount = Number(formData.discount) || 0;
                                    const invoiceDiscountType = formData.discountType || 'fixed';
                                    
                                    // حساب معامل الخصم الفعلي (فقط على المنتجات غير الخدمية)
                                    let discountFactor = 1;
                                    if (invoiceDiscount > 0) {
                                      if (invoiceDiscountType === 'percentage') {
                                        // إذا كان الخصم نسبة مئوية
                                        discountFactor = 1 - (invoiceDiscount / 100);
                                      } else {
                                        // إذا كان الخصم مبلغاً ثابتاً
                                        const subtotal = formData.items.reduce((total, i) => total + (i.total || 0), 0);
                                        const subtotalNonService = formData.items.reduce((total, i) => {
                                          return i.isServiceProduct ? total : total + (i.total || 0);
                                        }, 0);
                                        const baseSubtotal = subtotalNonService > 0 ? subtotalNonService : subtotal;
                                        if (baseSubtotal > 0) {
                                          discountFactor = Math.max(0, 1 - (invoiceDiscount / baseSubtotal));
                                        }
                                      }
                                    }
                                    
                                    // حساب قيمة الضريبة بعد تطبيق الخصم
                                    // المنتجات الخدمية لا تتأثر بالخصم الكلي
                                    const priceWithDiscount = item.total / item.quantity; // سعر الوحدة بعد الخصم
                                    const taxRate = item.tax / 100;
                                    const priceBeforeTax = priceWithDiscount / (1 + taxRate);
                                    const taxAmount = priceWithDiscount - priceBeforeTax;
                                    const discountedTaxAmount = item.isServiceProduct ? taxAmount * item.quantity : taxAmount * discountFactor * item.quantity; // ضرب في الكمية للحصول على إجمالي الضريبة
                                    
                                    return discountedTaxAmount.toFixed(2);
                                  })()}
                                </Typography>
                              </TableCell>
                            </>
                          ) : (
                            <TableCell>
                              <TextField
                                variant="outlined"
                                type="number"
                                size="small"
                                value={item.price}
                                disabled={page?.lockPrice || false}
                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                sx={{ width: 100 }}
                              />
                              {item.barcode && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  باركود: {item.barcode}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          
                          {/* Quantity Cell */}
                          <TableCell>
                            <TextField
                              variant="outlined"
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                // إذا كان الخيار مفعل، تحويل القيمة إلى رقم صحيح
                                if (page?.integerQuantityOnly) {
                                  const intValue = Math.floor(parseFloat(value) || 0);
                                  handleItemChange(index, 'quantity', intValue > 0 ? intValue : 1);
                                } else {
                                  handleItemChange(index, 'quantity', value);
                                }
                              }}
                              InputProps={{
                                inputProps: {
                                  min: page?.integerQuantityOnly ? 1 : 0.01,
                                  step: page?.integerQuantityOnly ? 1 : 0.01
                                }
                              }}
                              sx={{ width: 80 }}
                            />
                            {item.unit && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.unit}
                              </Typography>
                            )}
                          </TableCell>
                          
                          {/* Discount Cell */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TextField
                                variant="outlined"
                                type="number"
                                size="small"
                                value={item.discount}
                                disabled={page?.lockProductDiscount || false}
                                onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                InputProps={{
                                  inputProps: { min: 0, step: 0.01 },
                                  endAdornment:
                                    <InputAdornment position="end">
                                      {item.discountType === 'percentage' ? '%' : ''}
                                    </InputAdornment>
                                }}
                                sx={{ width: 80 }}
                              />
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="خصم ثابت">
                                  <Checkbox
                                    checked={item.discountType === 'fixed'}
                                    disabled={page?.lockProductDiscount || false}
                                    onChange={() => handleItemChange(index, 'discountType', 'fixed')}
                                    size="small"
                                    icon={<span style={{ fontSize: '0.7rem' }}>ر</span>}
                                    checkedIcon={<span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>ر</span>}
                                  />
                                </Tooltip>
                                <Tooltip title="خصم نسبة">
                                  <Checkbox
                                    checked={item.discountType === 'percentage'}
                                    disabled={page?.lockProductDiscount || false}
                                    onChange={() => handleItemChange(index, 'discountType', 'percentage')}
                                    size="small"
                                    icon={<span style={{ fontSize: '0.7rem' }}>%</span>}
                                    checkedIcon={<span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>%</span>}
                                  />
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          {/* Total Cell */}
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" key={`${formData.discount ?? 0}-${formData.discountType ?? 'fixed'}-${isLoadedForEdit}`}>
                              {(() => {
                                // إذا كانت الفاتورة محملة للتعديل، لا نطبق خصم إضافي
                                if (isLoadedForEdit) {
                                  // عرض المجموع كما هو محفوظ في قاعدة البيانات
                                  return item.total.toFixed(2);
                                }
                                
                                // للفواتير الجديدة، نحسب معامل الخصم الإجمالي للفاتورة
                                const invoiceDiscount = Number(formData.discount) || 0;
                                const invoiceDiscountType = formData.discountType || 'fixed';
                                
                                // حساب معامل الخصم الفعلي (فقط على المنتجات غير الخدمية)
                                let discountFactor = 1;
                                if (invoiceDiscount > 0) {
                                  if (invoiceDiscountType === 'percentage') {
                                    // إذا كان الخصم نسبة مئوية
                                    discountFactor = 1 - (invoiceDiscount / 100);
                                  } else {
                                    // إذا كان الخصم مبلغاً ثابتاً
                                    const subtotal = formData.items.reduce((total, i) => total + (i.total || 0), 0);
                                    const subtotalNonService = formData.items.reduce((total, i) => {
                                      return i.isServiceProduct ? total : total + (i.total || 0);
                                    }, 0);
                                    const baseSubtotal = subtotalNonService > 0 ? subtotalNonService : subtotal;
                                    if (baseSubtotal > 0) {
                                      discountFactor = Math.max(0, 1 - (invoiceDiscount / baseSubtotal));
                                    }
                                  }
                                }
                                
                                // حساب المجموع بعد تطبيق الخصم
                                // المنتجات الخدمية لا تتأثر بالخصم الكلي
                                const totalAfterDiscount = item.isServiceProduct ? item.total : item.total * discountFactor;
                                
                                return totalAfterDiscount.toFixed(2);
                              })()}
                            </Typography>
                            {!isLoadedForEdit && (Number(formData.discount) ?? 0) > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                {item.total.toFixed(2)}
                              </Typography>
                            )}
                          </TableCell>
                          
                          {/* Delete Cell */}
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveItem(index)}
                              size="small"
                              sx={{ 
                                '&:hover': { bgcolor: '#ffebee' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                    )}
                  </TableBody>
                </Table>
              </Box>
              
              {/* إضافة Pagination في الأسفل أيضاً */}
              {formData.items.length > 50 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={formData.items.length}
                    page={tablePage}
                    onPageChange={(_, newPage) => setTablePage(newPage)}
                    rowsPerPage={tableRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setTableRowsPerPage(parseInt(e.target.value, 10));
                      setTablePage(0);
                    }}
                    rowsPerPageOptions={[50, 100, 200, 500]}
                    labelRowsPerPage="عدد الصفوف:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Product Attribute Search Dialog */}
      <ProductAttributeSearch
        open={isProductSearchOpen}
        onClose={() => setIsProductSearchOpen(false)}
        onSelectVariant={handleAttributeVariantSelect}
        onSelectVariants={handleMultiProductSelect}
        selectedCategories={page?.selectedCategories || []}
        productSelectionMode={page?.productSelectionMode || 'all'}
      />
      
      {/* Customer Details Dialog */}
      <CustomerDetailsDialog
        open={isCustomerDetailsOpen}
        onClose={() => setIsCustomerDetailsOpen(false)}
        onSave={handleSaveNewCustomer}
        onUpdate={handleUpdateCustomer}
        customerId={selectedCustomer?._id}
        initialData={{
          name: selectedCustomer?.name || customerName,
          phone: selectedCustomer?.phone || customerPhone,
          email: selectedCustomer?.email || '',
          taxNumber: selectedCustomer?.taxNumber || '',
          address: selectedCustomer?.address || undefined,
          notes: selectedCustomer?.notes ? (Array.isArray(selectedCustomer.notes)
            ? selectedCustomer.notes.map((note: any) => note.content).join('\n')
            : selectedCustomer.notes) : '',
          phoneDigitsLimit: page?.customerPhoneDigitsLimit || 10,
          type: 'customer',
          status: selectedCustomer?.status || 'active',
          category: selectedCustomer?.category || 'regular'
        }}
        branchId={activeBranch?._id}
        searchCustomersFromServer={searchCustomersFromServer} // تمرير دالة البحث من الخادم
      />
      
      {/* Customer Search Results Dialog */}
      <CustomerSearchResults
        open={isCustomerSearchOpen}
        onClose={() => setIsCustomerSearchOpen(false)}
        customers={matchingCustomers}
        onSelectCustomer={handleSelectCustomerFromSearch}
        searchTerm={customerSearchTerm}
      />

      {/* Barcode Message Snackbar */}
      <Snackbar
        open={showBarcodeMessage}
        autoHideDuration={3000}
        onClose={() => setShowBarcodeMessage(false)}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={barcodeMessage.includes('تم') ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {barcodeMessage}
        </Alert>
      </Snackbar>

      {/* نافذة البحث عن خدمات المنتج */}
      <ServiceProductSearchDialog
        open={isServiceProductSearchOpen}
        onClose={handleCloseServiceProductSearch}
        onSelectService={handleSelectServiceProduct}
        paymentMethods={availablePaymentMethods}
      />

      {/* نافذة النجاح مع الخيارات */}
      <Dialog
        open={showSuccessDialog}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              تم حفظ الفاتورة بنجاح!
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              تم حفظ الفاتورة رقم <strong>{savedInvoiceNumber || formData.invoiceNumber}</strong> بنجاح.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ماذا تريد أن تفعل الآن؟
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={handlePrintInvoice}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 }
                }}
              >
                طباعة الفاتورة
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<AddCircleIcon />}
                onClick={resetFormForNewInvoice}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 }
                }}
              >
                فاتورة جديدة
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<ListIcon />}
                onClick={() => {
                  handleBackToList();
                  handleCloseSuccessDialog();
                }}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                قائمة الفواتير
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* نافذة المنتجات غير الموجودة */}
      <Dialog
        open={showNotFoundDialog}
        onClose={() => setShowNotFoundDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              ⚠️ منتجات غير موجودة ({notFoundProducts.length})
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            لم يتم العثور على المنتجات التالية في قاعدة البيانات. يرجى التحقق من الباركودات أو إضافة المنتجات أولاً.
          </Alert>
          
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fff3e0' }}>
                  <TableCell><strong>رقم السطر</strong></TableCell>
                  <TableCell><strong>الباركود</strong></TableCell>
                  <TableCell><strong>اسم المنتج</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notFoundProducts.map((item, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fff8e1' } }}>
                    <TableCell>{item.rowNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#d32f2f' }}>
                        {item.barcode}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.productName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowNotFoundDialog(false)}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2
            }}
          >
            حسناً، فهمت
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceFormPage;
